import React, { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import {
  Upload, PlusCircle, Link as LinkIcon, Trash2, MousePointer2,
  Ruler, CheckCircle2, RotateCcw, Square, Package, Image as ImageIcon,
  Move, X, RefreshCw, ArrowRightLeft, Hand, Crop, Maximize2, Minimize2, Eye, Edit2, Box, Building2, LayoutGrid,
  Copy, ClipboardPaste
} from 'lucide-react';
import * as pdfjsLib from 'pdfjs-dist';
import workerSrc from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
import Map3DViewer from './Map3DViewer';
import PorscheConfiguratorPanel from './PorscheConfiguratorPanel';
import {
  getOrRequestModelSnapshot,
  detectModelType,
  drawPowerPalletTruck,
  drawPalletStationWithGuides,
  drawInboundScannerGantry,
  drawPalletProfileChecker,
  drawDesktopWorkstation
} from './modelSnapshotHelper';

pdfjsLib.GlobalWorkerOptions.workerSrc = workerSrc;

// ─── Constants ───────────────────────────────────────────────────────
const AREA_COLORS = {
  pickup:  { fill: 'rgba(16,185,129,0.18)', stroke: '#10b981', label: 'Pickup' },
  dropoff: { fill: 'rgba(239,68,68,0.18)',   stroke: '#ef4444', label: 'Drop-off' },
  storage: { fill: 'rgba(139,92,246,0.18)',  stroke: '#8b5cf6', label: 'Storage' },
  storage_selective_rack: { fill: 'rgba(14,165,233,0.18)', stroke: '#0ea5e9', label: 'Selective Rack' },
  storage_2way_shuttle: { fill: 'rgba(245,158,11,0.18)', stroke: '#f59e0b', label: '2-Way Shuttle Rack' },
  storage_4way_shuttle: { fill: 'rgba(236,72,153,0.18)', stroke: '#ec4899', label: '4-Way Shuttle Rack' },
  conveyor: { fill: 'rgba(75,85,99,0.18)', stroke: '#4b5563', label: 'Conveyor' },
  operator_station: { fill: 'rgba(16,185,129,0.18)', stroke: '#059669', label: 'Workstation' },
  model_3d: { fill: 'rgba(139,92,246,0.18)', stroke: '#8b5cf6', label: '3D Asset' },
};

const CORRIDOR_COLOR = { fill: 'rgba(251,191,36,0.15)', stroke: '#f59e0b', selectedFill: 'rgba(251,191,36,0.30)' };

const WORKFLOW_STEPS = [
  { id: 1, label: 'Upload Layout',      icon: ImageIcon },
  { id: 2, label: 'Set Scale',          icon: Ruler },
  { id: 3, label: 'Pallet Size',        icon: Package },
  { id: 4, label: 'Define Areas',       icon: Square },
  { id: 5, label: 'Draw Corridors',     icon: MousePointer2 },
  { id: 6, label: 'Set Flows',          icon: ArrowRightLeft },
  { id: 7, label: 'Verify & Finish',    icon: CheckCircle2 },
];

const generateSvgUrl = (svgContent, activeLayers, svgLayers, bgOriginalViewBox) => {
  // Force lines to black and ensure they stay exactly 1px thick regardless of zoom
  let layerStyles = `path, line, polyline, circle, ellipse { 
    stroke: #000000 !important; 
    vector-effect: non-scaling-stroke !important;
    stroke-width: 1px !important;
  }\n`;
  if (svgLayers && activeLayers) {
    const inactive = svgLayers.filter(l => !activeLayers.includes(l));
    inactive.forEach(l => {
      const safe = l.replace(/"/g, '\\"');
      layerStyles += `g[data-layer-name="${safe}"] { display: none !important; }\n`;
    });
  }
  // Wrap the CSS in CDATA so the XML parser doesn't choke on special characters in layer names (like &)
  let styledSvg = svgContent.replace('</svg>', `<style><![CDATA[\n${layerStyles}\n]]></style></svg>`);
  
  // Strip any existing width/height and force absolute pixels for Canvas/WebGL compatibility
  styledSvg = styledSvg.replace(/<svg\s+([^>]*?)>/i, (match, attrs) => {
    let newAttrs = attrs.replace(/\b(width|height)="[^"]*"/ig, '');
    if (bgOriginalViewBox) {
      const maxDim = 2048;
      let w = bgOriginalViewBox.w;
      let h = bgOriginalViewBox.h;
      if (w > maxDim || h > maxDim) {
        const scale = Math.min(maxDim / w, maxDim / h);
        w = Math.floor(w * scale);
        h = Math.floor(h * scale);
      }
      newAttrs += ` width="${w}px" height="${h}px"`;
    } else {
      newAttrs += ` width="2048px" height="2048px"`;
    }
    return `<svg ${newAttrs}>`;
  });
  
  // Return a Blob URL instead of base64 to support very large SVGs (like 3MB+)
  const blob = new Blob([styledSvg], { type: 'image/svg+xml;charset=utf-8' });
  return URL.createObjectURL(blob);
};

const NODE_SPACING = 50; // px between auto-generated nodes
const CONNECT_THRESHOLD = 35; // px proximity to auto-connect

// ─── Path generation ─────────────────────────────────────────────────
function generatePathNetwork(corridors, areas, deletedConns = new Set(), ppm = null) {
  const nodes = [];
  const edges = [];
  const connections = [];

  // 1. Generate line segments for all lanes in all corridors
  const segments = [];
  corridors.forEach(cor => {
    const isH = cor.w >= cor.h;
    let lanes = 1;
    if (cor.lanes) lanes = cor.lanes;
    else if (ppm) {
      const roadWidthMm = (isH ? cor.h : cor.w) / ppm;
      if (roadWidthMm >= 1600) lanes = 2;
    }
    const direction = cor.direction || 'bidirectional';

    if (isH) {
      if (lanes === 1) {
        segments.push({ corId: cor.id, isH, lane: 0, dir: direction === 'split' ? 'bidirectional' : direction, x1: cor.x, x2: cor.x + cor.w, y: cor.y + cor.h / 2, points: [] });
      } else {
        let dir1 = 'bidirectional', dir2 = 'bidirectional';
        if (direction === 'split') { dir1 = 'backward'; dir2 = 'forward'; }
        else if (direction === 'forward') { dir1 = 'forward'; dir2 = 'forward'; }
        else if (direction === 'backward') { dir1 = 'backward'; dir2 = 'backward'; }
        segments.push({ corId: cor.id, isH, lane: 0, dir: dir1, x1: cor.x, x2: cor.x + cor.w, y: cor.y + cor.h * 0.25, points: [] });
        segments.push({ corId: cor.id, isH, lane: 1, dir: dir2, x1: cor.x, x2: cor.x + cor.w, y: cor.y + cor.h * 0.75, points: [] });
      }
    } else {
      if (lanes === 1) {
        segments.push({ corId: cor.id, isH, lane: 0, dir: direction === 'split' ? 'bidirectional' : direction, y1: cor.y, y2: cor.y + cor.h, x: cor.x + cor.w / 2, points: [] });
      } else {
        let dir1 = 'bidirectional', dir2 = 'bidirectional';
        if (direction === 'split') { dir1 = 'backward'; dir2 = 'forward'; }
        else if (direction === 'forward') { dir1 = 'forward'; dir2 = 'forward'; }
        else if (direction === 'backward') { dir1 = 'backward'; dir2 = 'backward'; }
        segments.push({ corId: cor.id, isH, lane: 0, dir: dir1, y1: cor.y, y2: cor.y + cor.h, x: cor.x + cor.w * 0.25, points: [] });
        segments.push({ corId: cor.id, isH, lane: 1, dir: dir2, y1: cor.y, y2: cor.y + cor.h, x: cor.x + cor.w * 0.75, points: [] });
      }
    }
  });

  // Find explicit 90-degree intersections
  const hSegs = segments.filter(s => s.isH);
  const vSegs = segments.filter(s => !s.isH);
  hSegs.forEach(h => {
    vSegs.forEach(v => {
      if (v.x >= h.x1 && v.x <= h.x2 && h.y >= v.y1 && h.y <= v.y2) {
        h.points.push({ x: v.x, y: h.y, isIntersection: true });
        v.points.push({ x: v.x, y: h.y, isIntersection: true });
      }
    });
  });

  // Generate nodes for each segment
  segments.forEach(seg => {
    if (seg.isH) {
      const count = Math.max(2, Math.floor((seg.x2 - seg.x1) / NODE_SPACING) + 1);
      const step = (seg.x2 - seg.x1) / (count - 1);
      for (let i = 0; i < count; i++) {
        seg.points.push({ x: seg.x1 + i * step, y: seg.y, isIntersection: false });
      }
      seg.points.sort((a, b) => a.x - b.x);
    } else {
      const count = Math.max(2, Math.floor((seg.y2 - seg.y1) / NODE_SPACING) + 1);
      const step = (seg.y2 - seg.y1) / (count - 1);
      for (let i = 0; i < count; i++) {
        seg.points.push({ x: seg.x, y: seg.y1 + i * step, isIntersection: false });
      }
      seg.points.sort((a, b) => a.y - b.y);
    }

    // Deduplicate closely spaced points
    const uniquePoints = [];
    seg.points.forEach(p => {
      if (uniquePoints.length === 0) uniquePoints.push(p);
      else {
        const last = uniquePoints[uniquePoints.length - 1];
        if (Math.hypot(p.x - last.x, p.y - last.y) > 2) uniquePoints.push(p);
        else if (p.isIntersection) last.isIntersection = true;
      }
    });

    const laneNodes = [];
    uniquePoints.forEach((p, idx) => {
      let id = `${seg.corId}_l${seg.lane}_n${idx}`;
      if (p.isIntersection) id = `intersect_${Math.round(p.x)}_${Math.round(p.y)}`;
      laneNodes.push({ id, x: p.x, y: p.y, type: p.isIntersection ? 'intersection' : 'normal', corridorId: seg.corId, lane: seg.lane });
    });

    nodes.push(...laneNodes);

    for (let i = 0; i < laneNodes.length - 1; i++) {
      if (seg.dir === 'bidirectional' || seg.dir === 'forward') {
        edges.push({ from: laneNodes[i].id, to: laneNodes[i + 1].id, auto: true, dir: seg.dir === 'bidirectional' ? 'bi' : 'uni' });
      }
      if (seg.dir === 'bidirectional' || seg.dir === 'backward') {
        edges.push({ from: laneNodes[i + 1].id, to: laneNodes[i].id, auto: true, dir: seg.dir === 'bidirectional' ? 'bi' : 'uni' });
      }
    }
  });

  // Remove exact duplicate nodes (like shared intersection nodes)
  const finalNodes = [];
  const nodeIds = new Set();
  nodes.forEach(n => {
    if (!nodeIds.has(n.id)) {
      nodeIds.add(n.id);
      finalNodes.push(n);
    }
  });
  nodes.length = 0;
  nodes.push(...finalNodes);

  // 2. Connect nearby nodes from different corridors (only for parallel corridors)
  for (let i = 0; i < nodes.length; i++) {
    for (let j = i + 1; j < nodes.length; j++) {
      if (nodes[i].corridorId === nodes[j].corridorId) continue;
      
      const corI = corridors.find(c => c.id === nodes[i].corridorId);
      const corJ = corridors.find(c => c.id === nodes[j].corridorId);
      if (!corI || !corJ) continue;
      
      const isHI = corI.w >= corI.h;
      const isHJ = corJ.w >= corJ.h;
      // H and V corridors are fully handled by Step 1 explicit intersections.
      // Only use Step 2 to connect H-H or V-V corridors that touch end-to-end.
      if (isHI !== isHJ) continue;

      const dist = Math.hypot(nodes[i].x - nodes[j].x, nodes[i].y - nodes[j].y);
      if (dist < CONNECT_THRESHOLD) {
        // Prevent diagonal connections
        const isOrthogonal = Math.abs(nodes[i].x - nodes[j].x) < 2 || Math.abs(nodes[i].y - nodes[j].y) < 2;
        if (!isOrthogonal) continue;
        
        const connKey = [nodes[i].corridorId, nodes[j].corridorId].sort().join('__');
        if (deletedConns.has(connKey)) continue;
        const edgeExists = edges.some(e =>
          (e.from === nodes[i].id && e.to === nodes[j].id) ||
          (e.from === nodes[j].id && e.to === nodes[i].id)
        );
        if (!edgeExists) {
          edges.push({ from: nodes[i].id, to: nodes[j].id, auto: true, dir: 'bi' });
          edges.push({ from: nodes[j].id, to: nodes[i].id, auto: true, dir: 'bi' });
          if (!connections.find(c => c.key === connKey)) {
            connections.push({
              key: connKey,
              type: 'corridor-corridor',
              fromId: nodes[i].corridorId,
              toId: nodes[j].corridorId,
              fromLabel: corridors.find(c => c.id === nodes[i].corridorId)?.label || nodes[i].corridorId.substring(0, 10),
              toLabel: corridors.find(c => c.id === nodes[j].corridorId)?.label || nodes[j].corridorId.substring(0, 10),
            });
          }
        }
      }
    }
  }

  // 3. Connect corridor nodes near station areas
  areas.forEach(area => {
    nodes.forEach(node => {
      const pad = 40; // Reasonable padding for nearby connections
      const nearX = node.x >= area.x - pad && node.x <= area.x + area.w + pad;
      const nearY = node.y >= area.y - pad && node.y <= area.y + area.h + pad;
      const onEdgeH = Math.abs(node.y - area.y) < pad || Math.abs(node.y - (area.y + area.h)) < pad;
      const onEdgeV = Math.abs(node.x - area.x) < pad || Math.abs(node.x - (area.x + area.w)) < pad;
      
      const insideX = node.x >= area.x && node.x <= area.x + area.w;
      const insideY = node.y >= area.y && node.y <= area.y + area.h;

      if ((nearX && nearY && (onEdgeH || onEdgeV)) || (insideX && insideY)) {
        const connKey = [node.corridorId, area.id].sort().join('__');
        const nodeConnKey = [node.id, area.id].sort().join('__');
        if (deletedConns.has(connKey) || deletedConns.has(nodeConnKey)) return;
        node.type = area.type;
        node.connectedAreaId = area.id; // legacy
        if (!node.connectedAreaIds) node.connectedAreaIds = [];
        if (!node.connectedAreaIds.includes(area.id)) node.connectedAreaIds.push(area.id);
        
        if (!connections.find(c => c.key === connKey)) {
          const corLabel = corridors.find(c => c.id === node.corridorId)?.label || node.corridorId.substring(0, 10);
          const areaLabel = area.label || (AREA_COLORS[area.type]?.label || area.type);
          connections.push({
            key: connKey,
            type: 'corridor-station',
            fromId: node.corridorId,
            toId: area.id,
            fromLabel: corLabel,
            toLabel: areaLabel,
            stationType: area.type,
          });
        }
      }
    });
  });

  return { nodes, edges, connections };
}

// ─── Component ───────────────────────────────────────────────────────
export default function MapEditor({ 
  mapData, 
  setMapData, 
  activeSection = 'layout', 
  setActiveSection, 
  onNavigateTab 
}) {
  const canvasRef = useRef(null);
  const panRef = useRef({ active: false, x: 0, y: 0 });
  const committedViewTransformRef = useRef({ x: 0, y: 0, scale: 1 });
  const hasExistingContent = !!(mapData.bgImageData || mapData.svgContent || mapData.areas?.length > 0 || mapData.corridors?.length > 0);
  const [isStarted, setIsStarted] = useState(hasExistingContent);

  useEffect(() => {
    if (hasExistingContent) {
      setIsStarted(true);
    }
  }, [hasExistingContent]);

  const [activeStep, setActiveStep] = useState(hasExistingContent ? 4 : 1);
  const [bgImage, setBgImage] = useState(null);
  
  useEffect(() => {
    if (mapData.bgImageData) {
      const img = new Image();
      img.onload = () => setBgImage(img);
      img.src = mapData.bgImageData;
    } else {
      setBgImage(null);
    }
  }, [mapData.bgImageData]);


  const [isCalibrating, setIsCalibrating] = useState(false);
  const [calibrationHoverPos, setCalibrationHoverPos] = useState(null);

  const scaleCalibration = mapData.scaleCalibration || { point1: null, point2: null, distanceMm: '', pixelsPerMm: null };
  const setScaleCalibration = (updater) => {
    setMapData(prev => {
      const current = prev.scaleCalibration || { point1: null, point2: null, distanceMm: '', pixelsPerMm: null };
      return { ...prev, scaleCalibration: typeof updater === 'function' ? updater(current) : updater };
    });
  };

  const isCalibratingActive = isCalibrating || (!!mapData.bgImageData && !scaleCalibration?.pixelsPerMm);
  const [mode, setMode] = useState(hasExistingContent ? 'draw_area' : 'select');
  const [displayMode, setDisplayMode] = useState('view'); // 'view', 'edit', or '3d'
  const [is3DPositionLocked, setIs3DPositionLocked] = useState(true);
  const [show3DDimensions, setShow3DDimensions] = useState(true);
  const [cameraPreset, setCameraPreset] = useState({ type: 'isometric', ts: Date.now() });

  const handleStartBlank = () => {
    setIsStarted(true);
    if (!mapData.blankWarehouse) {
      createBlankWarehouse(40, 60, 10);
    }
    setActiveStep(3);
    setMode('select');
    if (setActiveSection) {
      setActiveSection('pallets');
    }
  };
  const [selectedNode, setSelectedNode] = useState(null);
  const [selectedArea, setSelectedArea] = useState(null);
  const [selectedAreaIds, setSelectedAreaIds] = useState([]);
  const [selectedCorridor, setSelectedCorridor] = useState(null);
  const [selectedCorridorIds, setSelectedCorridorIds] = useState([]);
  const [selectionBox, setSelectionBox] = useState(null); // { startX, startY, curX, curY, isShift }
  const selectionBoxRef = useRef(null);
  const [toast, setToast] = useState(null);
  const toastTimeoutRef = useRef(null);
  const showToast = useCallback((msg, type = 'info') => {
    if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    setToast({ msg, type });
    toastTimeoutRef.current = setTimeout(() => setToast(null), 3000);
  }, []);
  const clipboardRef = useRef(null);
  const [hasClipboard, setHasClipboard] = useState(false);
  const pasteCountRef = useRef(1);
  const hasDraggedRef = useRef(false);
  const dragStartPosRef = useRef({ x: 0, y: 0 });
  const [drawingRect, setDrawingRect] = useState(null);
  const [measuringLine, setMeasuringLine] = useState(null);
  const viewTransformRef = useRef({ x: 0, y: 0, scale: 1 });
  const bgImageRef = useRef(null);
  const available3DModelsRef = useRef([]);
  const rafRef = useRef(null);
  const drawMapRef = useRef(null);
  useEffect(() => {
    fetch('/api/models-3d')
      .then(res => res.json())
      .then(json => {
        if (json.status === 'success' && Array.isArray(json.data)) {
          available3DModelsRef.current = json.data;
        }
      })
      .catch(() => {});
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  const [autoConnections, setAutoConnections] = useState([]);
  const [isPanning, setIsPanning] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [selectingForFlow, setSelectingForFlow] = useState(null);
  const [dragState, setDragState] = useState(null); // { type, itemType, targetId, startX, startY, initialRect, handle }
  const [activeRackType, setActiveRackType] = useState('storage_selective_rack');
  const [activeLaneType, setActiveLaneType] = useState('2way');
  const [blankSetup, setBlankSetup] = useState({
    tab: 'blank', // 'blank' or 'file'
    widthM: mapData.blankWarehouse?.widthM || 40,
    lengthM: mapData.blankWarehouse?.lengthM || 60,
    heightM: mapData.blankWarehouse?.heightM || 10
  });

  const createBlankWarehouse = useCallback((widthM = 40, lengthM = 60, heightM = 10) => {
    const wM = Math.max(5, parseFloat(widthM) || 40);
    const lM = Math.max(5, parseFloat(lengthM) || 60);
    const hM = Math.max(2, parseFloat(heightM) || 10);

    const canvasW = 800;
    const canvasH = 600;
    const marginPx = 45;
    const availW = canvasW - marginPx * 2;
    const availH = canvasH - marginPx * 2;

    const widthMm = wM * 1000;
    const lengthMm = lM * 1000;

    const ppm = Math.min(availW / widthMm, availH / lengthMm);
    const rectW = widthMm * ppm;
    const rectH = lengthMm * ppm;
    const rectX = (canvasW - rectW) / 2;
    const rectY = (canvasH - rectH) / 2;

    setMapData(prev => ({
      ...prev,
      blankWarehouse: {
        widthM: wM,
        lengthM: lM,
        heightM: hM,
        rect: { x: rectX, y: rectY, w: rectW, h: rectH }
      },
      scaleCalibration: {
        point1: { x: rectX, y: rectY },
        point2: { x: rectX + rectW, y: rectY },
        distanceMm: String(widthMm),
        pixelsPerMm: ppm
      },
      bgImageData: null,
      svgContent: null
    }));

    viewTransformRef.current = { x: 0, y: 0, scale: 1 };
    if (bgImageRef.current) {
      bgImageRef.current.style.transform = 'translate(0%, 0%) scale(1)';
    }
    const zoomEl = document.getElementById('zoom-percentage-display');
    if (zoomEl) zoomEl.innerText = '100%';

    setActiveStep(3);
    setMode('select');
  }, []);

  // We now use an <img> tag with a Blob URL for maximum performance and infinite sharpness.
  useEffect(() => {
    if (mapData.svgContent && (!mapData.svgLayers || mapData.svgLayers.length === 0)) {
      const layerNames = [];
      const layerRegex = /data-layer-name=(["'])(.*?)\1/g;
      let lMatch;
      while ((lMatch = layerRegex.exec(mapData.svgContent)) !== null) {
        const name = lMatch[2].replace(/&quot;/g, '"');
        if (!layerNames.includes(name)) layerNames.push(name);
      }
      if (layerNames.length > 0) {
        setMapData(p => ({ ...p, svgLayers: layerNames, activeLayers: layerNames }));
      }
    }
  }, [mapData.svgContent]);

  const [svgBlobUrl, setSvgBlobUrl] = useState(null);

  useEffect(() => {
    let url = null;
    if (mapData.svgContent && mapData.svgLayers && mapData.activeLayers) {
      url = generateSvgUrl(mapData.svgContent, mapData.activeLayers, mapData.svgLayers, mapData.bgOriginalViewBox);
      setSvgBlobUrl(url);
    } else {
      setSvgBlobUrl(null);
    }
    return () => {
      if (url && url.startsWith('blob:')) {
        URL.revokeObjectURL(url);
      }
    };
  }, [mapData.svgContent, mapData.activeLayers, mapData.svgLayers, mapData.bgOriginalViewBox]);

  // Step completion check
  const stepDone = useCallback((id) => {
    switch (id) {
      case 1: return true; // Open canvas is ready!
      case 2: return true; // Standard scale is ready!
      case 3: return (mapData.palletTypes?.length > 0) || (mapData.palletSize?.width > 0);
      case 4: return (mapData.areas?.length || 0) > 0;
      case 5: return (mapData.corridors?.length || 0) > 0;
      case 6: return (mapData.flows?.length || 0) > 0;
      default: return false;
    }
  }, [mapData]);

  const prevStepRef = useRef(null);
  useEffect(() => {
    if (prevStepRef.current !== activeStep) {
      prevStepRef.current = activeStep;
      if (activeStep !== 4 && activeStep !== 6) {
        setSelectedArea(null);
        setSelectedAreaIds([]);
      }
      if (activeStep !== 5) {
        setSelectedCorridor(null);
        setSelectedCorridorIds([]);
      }
      setSelectedNode(null);
      setSelectionBox(null);
      if (selectionBoxRef.current) selectionBoxRef.current = null;
      if (activeStep === 4) setMode((mapData.areas && mapData.areas.length > 0) ? 'select' : 'draw_area');
      else if (activeStep === 5) setMode((mapData.corridors && mapData.corridors.length > 0) ? 'select' : 'draw_corridor');
      else setMode('select');
      setSelectingForFlow(null);
    }
  }, [activeStep]);

  // Prevent default scroll/zoom on canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const preventDefault = (e) => {
      e.preventDefault();
    };
    canvas.addEventListener('wheel', preventDefault, { passive: false });
    return () => canvas.removeEventListener('wheel', preventDefault);
  }, []);

  const deletedConns = useMemo(() => new Set(mapData.deletedConns || []), [mapData.deletedConns]);
  // ─── Regenerate paths ──────────────────────────────────────────────
  const regeneratePaths = useCallback(() => {
    const cors = mapData.corridors || [];
    if (cors.length === 0) {
      setMapData(p => ({ ...p, nodes: [], edges: [] }));
      setAutoConnections([]);
      return;
    }
    const ppm = mapData.scaleCalibration?.pixelsPerMm;
    const result = generatePathNetwork(cors, mapData.areas, deletedConns, ppm);
    setMapData(p => ({ ...p, nodes: result.nodes, edges: result.edges }));
    setAutoConnections(result.connections);
  }, [mapData.corridors, mapData.areas, deletedConns, setMapData, mapData.scaleCalibration]);

  // Auto-regenerate when corridors, areas, or deleted connections change
  const prevCorridorsRef = useRef('');
  const prevAreasRef = useRef('');
  const prevDeletedRef = useRef('');
  useEffect(() => {
    const corKey = JSON.stringify(mapData.corridors || []);
    const areaKey = JSON.stringify(mapData.areas);
    const delKey = JSON.stringify([...deletedConns]);
    if (corKey !== prevCorridorsRef.current || areaKey !== prevAreasRef.current || delKey !== prevDeletedRef.current) {
      prevCorridorsRef.current = corKey;
      prevAreasRef.current = areaKey;
      prevDeletedRef.current = delKey;
      regeneratePaths();
    }
  }, [mapData.corridors, mapData.areas, deletedConns, regeneratePaths]);

  // ─── Image upload ──────────────────────────────────────────────────
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const loadImage = (src) => {
      setMapData(p => {
        if (p.bgImageData && p.bgImageData.startsWith('blob:') && p.bgImageData !== src) {
          URL.revokeObjectURL(p.bgImageData);
        }
        return { 
          ...p, 
          bgImageData: src,
          scaleCalibration: { point1: null, point2: null, distanceMm: '', pixelsPerMm: null }
        };
      });
      setIsStarted(true);
      setIsCalibrating(true);
      setCalibrationHoverPos(null);
      setActiveStep(2);
      if (setActiveSection) {
        setActiveSection('layout');
      }
    };
    
    // Check if file is DWG (usually application/acad, image/vnd.dwg, or .dwg extension)
    if (file.name.toLowerCase().endsWith('.dwg')) {
      setIsUploading(true);
      const formData = new FormData();
      formData.append('file', file);
      let vw = 2000, vh = 2000, vx = 0, vy = 0;
      try {
        const response = await fetch('/api/convert-dwg', {
          method: 'POST',
          body: formData
        });
        if (!response.ok) throw new Error('Conversion failed');
        let svgText = await response.text();
        
        // Parse SVG and forcefully inject width/height to fix canvas rendering
        try {
          const parser = new DOMParser();
          const doc = parser.parseFromString(svgText, "image/svg+xml");
          const parsedSvgEl = doc.documentElement;
          
          if (parsedSvgEl.tagName.toLowerCase() === 'svg') {
            const importedSvg = document.importNode(parsedSvgEl, true);
            
            // Wrap everything inside a <g> to compute true bounding box
            const wrapper = document.createElementNS("http://www.w3.org/2000/svg", "g");
            while (importedSvg.firstChild) wrapper.appendChild(importedSvg.firstChild);
            importedSvg.appendChild(wrapper);

            // Fix invisible microscopic lines caused by huge viewBox scaling
            const style = document.createElementNS("http://www.w3.org/2000/svg", "style");
            style.textContent = '* { vector-effect: non-scaling-stroke !important; stroke-width: 1px !important; }';
            importedSvg.insertBefore(style, importedSvg.firstChild);

            // Append to DOM to calculate true BBox
            const tempDiv = document.createElement('div');
            tempDiv.style.visibility = 'hidden';
            tempDiv.style.position = 'absolute';
            tempDiv.style.width = '0px';
            tempDiv.style.height = '0px';
            tempDiv.style.overflow = 'hidden';
            document.body.appendChild(tempDiv);
            tempDiv.appendChild(importedSvg);
            
            try {
              const bbox = wrapper.getBBox();
              if (bbox && bbox.width > 0 && bbox.height > 0) {
                vw = bbox.width;
                vh = bbox.height;
                vx = bbox.x;
                vy = bbox.y;
              }
            } catch (e) { console.warn("getBBox failed", e); }
            
            document.body.removeChild(tempDiv);
            
            importedSvg.setAttribute('viewBox', `${vx} ${vy} ${vw} ${vh}`);
            importedSvg.setAttribute('width', '100%');
            importedSvg.setAttribute('height', '100%');
            importedSvg.setAttribute('preserveAspectRatio', 'xMidYMid meet');
            svgText = new XMLSerializer().serializeToString(importedSvg);
          }
        } catch (e) {
          console.warn("Could not parse SVG to inject dimensions", e);
        }
        
        // Extract layer names from the SVG
        const layerNames = [];
        // Regex on svgText (may or may not have ran through XMLSerializer already)
        const layerRegex = /data-layer-name="([^"]+)"/g;
        let lMatch;
        while ((lMatch = layerRegex.exec(svgText)) !== null) {
          const name = lMatch[1].replace(/&quot;/g, '"');
          if (!layerNames.includes(name)) layerNames.push(name);
        }
        // Also try getAttribute from parsed DOM as fallback (handles namespace variants)
        try {
          const domDoc = new DOMParser().parseFromString(svgText, 'image/svg+xml');
          const layerEls = domDoc.querySelectorAll('[data-layer-name]');
          layerEls.forEach(el => {
            const name = el.getAttribute('data-layer-name');
            if (name && !layerNames.includes(name)) layerNames.push(name);
          });
        } catch(le) { console.warn('Layer DOM fallback failed', le); }
        console.log('[DWG Upload] Layers found:', layerNames, '| SVG snippet:', svgText.substring(0, 500));
        
        const dwgScale = Math.min(800 / vw, 600 / vh);
        setMapData(p => ({
          ...p,
          svgContent: svgText,
          svgLayers: layerNames,
          activeLayers: layerNames,
          deletedLines: [],
          bgImageData: null,
          bgOriginalViewBox: { x: vx, y: vy, w: vw, h: vh },
          scaleCalibration: { point1: {x:0, y:0}, point2: {x:0, y:0}, distanceMm: 'Auto 1:1', pixelsPerMm: dwgScale }
        }));
        setIsUploading(false);
        // setActiveStep(2); // Auto-advance disabled per user request
      } catch (err) {
        console.error(err);
        setIsUploading(false);
        alert('Failed to convert DWG file. Please ensure the backend DWG service is running.');
      }
      return;
    }

    const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
    if (isPdf) {
      const fr = new FileReader();
      fr.onload = async function () {
        try {
          const pdf = await pdfjsLib.getDocument({
            data: new Uint8Array(this.result),
            standardFontDataUrl: `https://unpkg.com/pdfjs-dist@${pdfjsLib.version}/standard_fonts/`,
          }).promise;
          const page = await pdf.getPage(1);
          const vp = page.getViewport({ scale: 2.0 });
          const c = document.createElement('canvas');
          c.width = vp.width; c.height = vp.height;
          await page.render({ canvasContext: c.getContext('2d'), viewport: vp }).promise;
          loadImage(c.toDataURL());
        } catch (e) { 
          console.error("PDF Load Error:", e);
          alert('Failed to load PDF: ' + (e.message || e.toString())); 
        }
      };
      fr.readAsArrayBuffer(file);
    } else {
      const r = new FileReader();
      r.onload = (ev) => loadImage(ev.target.result);
      r.readAsDataURL(file);
    }
  };

  // ─── Pallet capacity ──────────────────────────────────────────────
  const getPalletCapacity = useCallback((area) => {
    const ppm = scaleCalibration.pixelsPerMm;
    if (!ppm) return { cols: 0, rows: 0, total: 0, areaW: 0, areaH: 0 };
    const areaW = area.w / ppm, areaH = area.h / ppm;
    const defaultPallet = mapData.palletTypes?.[0] || mapData.palletSize;
    const { width: pw, length: pl } = defaultPallet;
    if (pw <= 0 || pl <= 0) return { cols: 0, rows: 0, total: 0, areaW, areaH };

    if (area.type === 'storage_2way_shuttle' || area.type === 'storage_4way_shuttle') {
      const margin = area.rackConfig?.margin ?? 100;
      const levels = area.rackConfig?.levels || 4;
      const beamThick = area.rackConfig?.beamThickness ?? 100;
      
      const laneWidth = pw + margin + beamThick;
      const laneDepth = pl + margin;
      
      let vTracks = area.rackConfig?.vTracks || 0;
      let hTracks = area.rackConfig?.hTracks || 0;
      
      const calcCap = (ew, eh, isVert) => {
        let c = Math.floor(ew / laneWidth);
        let r = Math.floor(eh / laneDepth);
        
        let screenCols = isVert ? r : c;
        let screenRows = isVert ? c : r;
        
        let vT = vTracks >= screenCols ? 0 : vTracks;
        let hT = hTracks >= screenRows ? 0 : hTracks;
        
        let maxTotal = screenCols * screenRows * levels;
        let total = (screenCols - vT) * (screenRows - hT) * levels;
        if (total < 0) total = 0;
        
        const getIndices = (count, tracks) => {
           const indices = [];
           for (let i = 1; i <= tracks; i++) {
             indices.push(Math.floor((count * i) / (tracks + 1)));
           }
           return indices;
        };
        
        let screenColIndices = getIndices(screenCols, vT);
        let screenRowIndices = getIndices(screenRows, hT);
        
        let rTrackIndices = isVert ? screenColIndices : screenRowIndices;
        let cTrackIndices = isVert ? screenRowIndices : screenColIndices;
        
        return { cols: c, rows: r, total, maxTotal, rTrackIndices, cTrackIndices };
      };
      
      const prefOri = area.rackConfig?.orientation || 'auto';
      let cols, rows, total, maxTotal, orientation, rTrackIndices, cTrackIndices;
      
      const capH = calcCap(areaW, areaH, false);
      const capV = calcCap(areaH, areaW, true);
      
      if (area.type === 'storage_4way_shuttle' || prefOri === 'horizontal' || (prefOri === 'auto' && capH.total >= capV.total)) {
        cols = capH.cols; rows = capH.rows; total = capH.total; maxTotal = capH.maxTotal;
        rTrackIndices = capH.rTrackIndices; cTrackIndices = capH.cTrackIndices;
        orientation = 'horizontal';
      } else {
        cols = capV.cols; rows = capV.rows; total = capV.total; maxTotal = capV.maxTotal;
        rTrackIndices = capV.rTrackIndices; cTrackIndices = capV.cTrackIndices;
        orientation = 'vertical';
      }
      
      return { cols, rows, total, maxTotal, rTrackIndices, cTrackIndices, areaW, areaH, margin, beamThick, laneWidth, laneDepth, orientation, levels };
    }

    if (area.type === 'storage_selective_rack') {
      const ppb = Number(area.rackConfig?.palletsPerBay) || 2;
      const margin = area.rackConfig?.margin !== undefined && area.rackConfig?.margin !== '' ? Number(area.rackConfig.margin) : 100;
      const levels = Number(area.rackConfig?.levels) || 4;
      const beamThick = area.rackConfig?.beamThickness !== undefined && area.rackConfig?.beamThickness !== '' ? Number(area.rackConfig.beamThickness) : 100;

      const backToBack = area.rackConfig?.backToBack !== undefined && area.rackConfig?.backToBack !== '' ? Number(area.rackConfig.backToBack) : 150;
      const aisleWidth = area.rackConfig?.aisleWidth !== undefined && area.rackConfig?.aisleWidth !== '' ? Number(area.rackConfig.aisleWidth) : 3000;

      const bayWidth = (ppb * pw) + ((ppb + 1) * margin);
      const blockSize = pl + backToBack + pl + aisleWidth;

      const singleRowStart = !!area.rackConfig?.singleRowStart;
      const calcCap = (w, h) => {
        let bays = Math.floor((w - beamThick) / (bayWidth + beamThick));
        if (bays < 0) bays = 0;
        let rows = 0;
        let remH = h;
        if (singleRowStart) {
          if (remH >= pl) {
            rows += 1;
            remH -= (pl + aisleWidth);
          }
        }
        if (remH >= pl) {
          const numBlocks = Math.floor(remH / blockSize);
          rows += numBlocks * 2;
          remH -= numBlocks * blockSize;
          if (remH >= (2 * pl) + backToBack) rows += 2;
          else if (remH >= pl) rows += 1;
        }
        return { bays, rows, total: bays * ppb * levels * rows };
      };

      const prefOri = area.rackConfig?.orientation || 'auto';
      let bays, rows, total, orientation;

      const capH = calcCap(areaW, areaH);
      const capV = calcCap(areaH, areaW);

      if (prefOri === 'horizontal' || (prefOri === 'auto' && capH.total >= capV.total)) {
        bays = capH.bays; rows = capH.rows; total = capH.total; orientation = 'horizontal';
      } else {
        bays = capV.bays; rows = capV.rows; total = capV.total; orientation = 'vertical';
      }

      return {
        cols: bays, rows: rows, total: total,
        areaW, areaH, bayWidth, beamThick, ppb, margin, levels,
        backToBack, aisleWidth, orientation
      };
    }

    if (area.type === 'conveyor') {
      const isHoriz = areaW >= areaH;
      const length = isHoriz ? areaW : areaH;
      const width = isHoriz ? areaH : areaW;
      const sideGapMm = area.conveyorConfig?.sideGapMm !== undefined ? area.conveyorConfig.sideGapMm : 50;
      
      let palletAlongMm, palletCrossMm;
      const prefOri = area.conveyorConfig?.palletOrientation || 'auto';
      if (prefOri === 'width_leading') {
        palletAlongMm = Math.min(pw, pl);
        palletCrossMm = Math.max(pw, pl);
      } else if (prefOri === 'length_leading') {
        palletAlongMm = Math.max(pw, pl);
        palletCrossMm = Math.min(pw, pl);
      } else {
        // Auto
        if (width >= pl + sideGapMm * 2) {
          palletCrossMm = pl;
          palletAlongMm = pw;
        } else if (width >= pw + sideGapMm * 2) {
          palletCrossMm = pw;
          palletAlongMm = pl;
        } else {
          palletCrossMm = Math.max(100, width - sideGapMm * 2);
          palletAlongMm = pw;
        }
      }
      const gapMm = 120;
      let count = Math.floor(length / (palletAlongMm + gapMm));
      if (count <= 0 && length >= palletAlongMm) count = 1;
      if (count * palletAlongMm > length) count = Math.floor(length / palletAlongMm);
      if (count < 0) count = 0;

      return { 
        cols: isHoriz ? count : 1, 
        rows: isHoriz ? 1 : count, 
        total: count, 
        areaW, areaH, levels: 1,
        palletAlongMm, palletCrossMm, gapMm, isHoriz, sideGapMm
      };
    }

    if (area.type === 'operator_station' || area.type === 'model_3d') {
      return { cols: 1, rows: 1, total: 0, areaW, areaH, levels: 1 };
    }

    if (area.type === 'storage' || area.type === 'storage_on_floor') {
      const prefOri = area.rackConfig?.orientation || 'auto';
      let palletW = pw, palletL = pl;
      if (prefOri === 'horizontal') {
        palletW = Math.max(pw, pl);
        palletL = Math.min(pw, pl);
      } else if (prefOri === 'vertical') {
        palletW = Math.min(pw, pl);
        palletL = Math.max(pw, pl);
      } else {
        const cap1 = Math.floor(areaW / pw) * Math.floor(areaH / pl);
        const cap2 = Math.floor(areaW / pl) * Math.floor(areaH / pw);
        if (cap2 > cap1) {
          palletW = pl; palletL = pw;
        }
      }
      const cols = Math.max(1, Math.floor(areaW / palletW));
      const rows = Math.max(1, Math.floor(areaH / palletL));
      const palletStatus = area.rackConfig?.palletStatus || 'full';
      const isAreaEmpty = palletStatus === 'empty';
      const emptyStack = area.rackConfig?.emptyPalletStackCount || (area.rackConfig?.levels || 1);
      const levels = isAreaEmpty ? emptyStack : (area.rackConfig?.levels || 1);
      const total = cols * rows * levels;
      return { cols, rows, total, palletW, palletL, levels, areaW, areaH, palletStatus };
    }

    return { cols: Math.floor(areaW / pw), rows: Math.floor(areaH / pl), total: Math.floor(areaW / pw) * Math.floor(areaH / pl), areaW, areaH };
  }, [scaleCalibration.pixelsPerMm, mapData.palletSize, mapData.palletTypes]);

  // ── 2D CAD Top-Down Graphic Renderer for 3D Models / AGVs ──
  const draw2DModelGraphic = (ctx, cx, cy, aw, ah, rotDeg, modelConfig, ppm, uiScale) => {
    const fileUrl = modelConfig?.fileUrl || modelConfig?.file_url;
    const targetH = modelConfig?.targetHeight || modelConfig?.target_height_m || 1.70;
    const detectedType = detectModelType(modelConfig);

    // 1. Request top-down snapshot from actual 3D GLB model with Three.js offscreen renderer
    const snapshot = fileUrl ? getOrRequestModelSnapshot(fileUrl, targetH, () => {
      if (drawMapRef.current) drawMapRef.current();
    }) : null;

    const meterPx = (ppm || 0.0125) * 1000;

    // Real physical dimensions (in meters)
    let realW_m = 1.28;
    let realL_m = 2.70;

    if (snapshot && snapshot.realWidthM && snapshot.realLengthM) {
      realW_m = snapshot.realWidthM;
      realL_m = snapshot.realLengthM;
    } else {
      if (detectedType === 'power_pallet') {
        realW_m = 0.73; // 730 mm (Toyota BT Levio LPE200)
        realL_m = 2.15; // 2150 mm
      } else if (detectedType === 'pallet_station') {
        realW_m = 1.20; // 1200 mm
        realL_m = 1.30; // 1300 mm
      } else if (detectedType === 'inbound_station') {
        realW_m = 2.40; // 2400 mm
        realL_m = 0.80; // 800 mm
      } else if (detectedType === 'pallet_checker') {
        realW_m = 1.60;
        realL_m = 0.80;
      } else if (detectedType === 'desktop_workstation') {
        realW_m = 1.40;
        realL_m = 0.85;
      } else if (detectedType === 'reach_truck') {
        realW_m = 1.28; // 1280 mm
        realL_m = 2.70; // 2700 mm
      } else if (detectedType === 'forklift') {
        realW_m = 1.18; // 1180 mm
        realL_m = 3.05; // 3050 mm
      } else if (detectedType === 'operator') {
        realW_m = 0.85; // 850 mm
        realL_m = 0.85; // 850 mm
      } else {
        realW_m = 1.40;
        realL_m = 1.40;
      }
    }

    const baseW_px = realW_m * meterPx;
    const baseL_px = realL_m * meterPx;

    // Rotation angle
    const normRot = ((rotDeg || 0) % 360 + 360) % 360;
    const rotRad = (normRot * Math.PI) / 180;
    const isRotSide = (Math.abs(normRot - 90) < 45) || (Math.abs(normRot - 270) < 45);
    const reqBoxW = isRotSide ? baseL_px : baseW_px;
    const reqBoxH = isRotSide ? baseW_px : baseL_px;

    // Fit scale:
    // If user draws large area (e.g. 7m x 5.8m zone), keep 100% true 1:1 real physical scale!
    // If user draws smaller box, scale down proportionally so it fits inside cleanly with 6% margin!
    const fitScale = Math.min(1.0, (aw * 0.94) / reqBoxW, (ah * 0.94) / reqBoxH);

    const truckW = Math.max(10 * uiScale, baseW_px * fitScale);
    const totalLen = Math.max(16 * uiScale, baseL_px * fitScale);

    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate(-rotRad); // 0 deg faces DOWN (+Y), 90 deg faces RIGHT (+X), matching 3D (+Z and +X)

    // ── Priority 1: Render Actual 3D Model Top-Down Snapshot ──
    if (snapshot && snapshot.image) {
      ctx.drawImage(snapshot.image, -truckW / 2, -totalLen / 2, truckW, totalLen);
      ctx.restore();
      return;
    }

    // ── Priority 2: High-Fidelity 2D CAD Procedural Fallbacks ──
    if (detectedType === 'power_pallet') {
      drawPowerPalletTruck(ctx, truckW, totalLen, uiScale);
    } else if (detectedType === 'pallet_station') {
      drawPalletStationWithGuides(ctx, truckW, totalLen, uiScale);
    } else if (detectedType === 'inbound_station') {
      drawInboundScannerGantry(ctx, truckW, totalLen, uiScale);
    } else if (detectedType === 'pallet_checker') {
      drawPalletProfileChecker(ctx, truckW, totalLen, uiScale);
    } else if (detectedType === 'desktop_workstation') {
      drawDesktopWorkstation(ctx, truckW, totalLen, uiScale);
    } else if (detectedType === 'reach_truck') {
      // Toyota Automated / Manual Reach Truck (RAE / RRE)
      const chassisL = totalLen * 0.58;
      const forkReachL = totalLen * 0.42;

      const rearY = -totalLen / 2;
      const mastY = rearY + chassisL;
      const forkTipY = totalLen / 2;

      const legW = Math.max(2.5 * uiScale, truckW * 0.19);
      const legL = forkReachL * 0.92;
      const forkW = Math.max(1.8 * uiScale, truckW * 0.10);
      const forkSpacing = truckW * 0.22; // Center-to-center = truckW * 0.44 (~560mm)

      // 1. Dual Outrigger Straddle Legs (Left & Right outer edges)
      [-truckW / 2 + legW / 2, truckW / 2 - legW / 2].forEach(legX => {
        // Outrigger steel beam
        ctx.fillStyle = '#1e293b';
        ctx.fillRect(legX - legW / 2, mastY - 3 * uiScale, legW, legL);

        // Front polyurethane load wheel
        ctx.fillStyle = '#64748b';
        ctx.fillRect(legX - legW / 2 + 1, mastY + legL - 6 * uiScale, legW - 2, 6 * uiScale);

        // Safety diagonal hazard warning stripes (/ / / /)
        ctx.save();
        ctx.beginPath();
        ctx.rect(legX - legW / 2, mastY, legW, legL - 7 * uiScale);
        ctx.clip();
        ctx.lineWidth = 1.8 * uiScale;
        const stripeGap = 5 * uiScale;
        for (let sy = mastY; sy < mastY + legL; sy += stripeGap * 1.5) {
          ctx.strokeStyle = '#facc15';
          ctx.beginPath();
          ctx.moveTo(legX - legW, sy);
          ctx.lineTo(legX + legW, sy + stripeGap);
          ctx.stroke();
          ctx.strokeStyle = '#0f172a';
          ctx.beginPath();
          ctx.moveTo(legX - legW, sy + stripeGap * 0.75);
          ctx.lineTo(legX + legW, sy + stripeGap * 1.75);
          ctx.stroke();
        }
        ctx.restore();

        // Front bumper LiDAR / obstacle sensor
        ctx.fillStyle = '#38bdf8';
        ctx.beginPath();
        ctx.arc(legX, mastY + legL - 1 * uiScale, 1.5 * uiScale, 0, Math.PI * 2);
        ctx.fill();
      });

      // 2. Dual Steel Forks
      [-forkSpacing, forkSpacing].forEach(fx => {
        ctx.fillStyle = '#e2e8f0';
        ctx.strokeStyle = '#475569';
        ctx.lineWidth = 1 * uiScale;
        ctx.beginPath();
        ctx.moveTo(fx - forkW / 2, mastY - 2 * uiScale);
        ctx.lineTo(fx - forkW / 2, forkTipY - 3 * uiScale);
        ctx.lineTo(fx, forkTipY);
        ctx.lineTo(fx + forkW / 2, forkTipY - 3 * uiScale);
        ctx.lineTo(fx + forkW / 2, mastY - 2 * uiScale);
        ctx.closePath();
        ctx.fill();
        ctx.stroke();

        // Fork top highlight
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(fx - forkW * 0.2, mastY, forkW * 0.4, (forkTipY - mastY) * 0.85);
      });

      // 3. Mast Assembly & Cross Beam
      ctx.fillStyle = '#0f172a';
      ctx.fillRect(-truckW * 0.40, mastY - 5 * uiScale, truckW * 0.80, 6 * uiScale);
      ctx.fillStyle = '#334155';
      ctx.fillRect(-truckW * 0.38, mastY - 8 * uiScale, 4.5 * uiScale, 10 * uiScale);
      ctx.fillRect(truckW * 0.38 - 4.5 * uiScale, mastY - 8 * uiScale, 4.5 * uiScale, 10 * uiScale);
      ctx.fillStyle = '#0284c7';
      ctx.fillRect(-2 * uiScale, mastY - 7 * uiScale, 4 * uiScale, 8 * uiScale);

      // 4. Main Chassis & Counterweight in Toyota Orange
      ctx.fillStyle = '#ea580c';
      ctx.strokeStyle = '#c2410c';
      ctx.lineWidth = 1.3 * uiScale;
      if (ctx.roundRect) {
        ctx.beginPath();
        ctx.roundRect(-truckW / 2, rearY, truckW, chassisL, [8 * uiScale, 8 * uiScale, 2 * uiScale, 2 * uiScale]);
        ctx.fill();
        ctx.stroke();
      } else {
        ctx.fillRect(-truckW / 2, rearY, truckW, chassisL);
        ctx.strokeRect(-truckW / 2, rearY, truckW, chassisL);
      }

      // Heavy rear counterweight casting
      ctx.fillStyle = '#0f172a';
      if (ctx.roundRect) {
        ctx.beginPath();
        ctx.roundRect(-truckW / 2 + 1, rearY + 1, truckW - 2, chassisL * 0.20, [7 * uiScale, 7 * uiScale, 0, 0]);
        ctx.fill();
      }

      // 5. Overhead Guard Canopy Roof
      const canopyW = truckW * 0.68;
      const canopyL = chassisL * 0.58;
      const canopyY = rearY + chassisL * 0.28;
      ctx.fillStyle = '#1e293b';
      ctx.strokeStyle = '#0f172a';
      ctx.lineWidth = 1.2 * uiScale;
      if (ctx.roundRect) {
        ctx.beginPath();
        ctx.roundRect(-canopyW / 2, canopyY, canopyW, canopyL, 3 * uiScale);
        ctx.fill();
        ctx.stroke();
      } else {
        ctx.fillRect(-canopyW / 2, canopyY, canopyW, canopyL);
      }

      // Grid bars
      ctx.strokeStyle = '#334155';
      ctx.lineWidth = 0.9 * uiScale;
      for (let gy = canopyY + 3.5 * uiScale; gy < canopyY + canopyL - 2 * uiScale; gy += 3.5 * uiScale) {
        ctx.beginPath();
        ctx.moveTo(-canopyW / 2 + 2 * uiScale, gy);
        ctx.lineTo(canopyW / 2 - 2 * uiScale, gy);
        ctx.stroke();
      }

      // 6. Autonomous RAE Sensors
      // LiDAR dome
      ctx.fillStyle = '#0284c7';
      ctx.beginPath();
      ctx.arc(-canopyW * 0.20, canopyY + canopyL * 0.25, 3 * uiScale, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = '#38bdf8';
      ctx.beginPath();
      ctx.arc(-canopyW * 0.20, canopyY + canopyL * 0.25, 1.5 * uiScale, 0, Math.PI * 2);
      ctx.fill();

      // E-Stop button
      ctx.fillStyle = '#ef4444';
      ctx.beginPath();
      ctx.arc(canopyW * 0.28, canopyY + canopyL * 0.20, 1.6 * uiScale, 0, Math.PI * 2);
      ctx.fill();

      // Direction Indicator Arrow at fork tips
      ctx.fillStyle = '#ef4444';
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 1 * uiScale;
      ctx.beginPath();
      ctx.moveTo(0, forkTipY + 4 * uiScale);
      ctx.lineTo(-3.5 * uiScale, forkTipY - 1.5 * uiScale);
      ctx.lineTo(3.5 * uiScale, forkTipY - 1.5 * uiScale);
      ctx.closePath();
      ctx.fill();
      ctx.stroke();

    } else if (detectedType === 'forklift') {
      // Toyota Counterbalance Forklift
      const chassisL = totalLen * 0.64;
      const forkL = totalLen * 0.36;
      const rearY = -totalLen / 2;
      const mastY = rearY + chassisL;
      const forkTipY = totalLen / 2;

      const forkW = Math.max(2 * uiScale, truckW * 0.10);
      const forkSpacing = truckW * 0.22;

      // 1. Dual Forks
      [-forkSpacing, forkSpacing].forEach(fx => {
        ctx.fillStyle = '#e2e8f0';
        ctx.strokeStyle = '#475569';
        ctx.lineWidth = 1 * uiScale;
        ctx.beginPath();
        ctx.moveTo(fx - forkW / 2, mastY - 2 * uiScale);
        ctx.lineTo(fx - forkW / 2, forkTipY - 3 * uiScale);
        ctx.lineTo(fx, forkTipY);
        ctx.lineTo(fx + forkW / 2, forkTipY - 3 * uiScale);
        ctx.lineTo(fx + forkW / 2, mastY - 2 * uiScale);
        ctx.closePath();
        ctx.fill();
        ctx.stroke();
      });

      // 2. Mast beam & Carriage
      ctx.fillStyle = '#0f172a';
      ctx.fillRect(-truckW * 0.42, mastY - 4 * uiScale, truckW * 0.84, 5 * uiScale);

      // 3. Front Wheels
      ctx.fillStyle = '#020617';
      const wheelW = Math.max(3 * uiScale, truckW * 0.13);
      const wheelL = Math.max(7 * uiScale, chassisL * 0.24);
      [-truckW / 2 - 1 * uiScale, truckW / 2 - wheelW + 1 * uiScale].forEach(wx => {
        ctx.fillRect(wx, mastY - wheelL - 2 * uiScale, wheelW, wheelL);
      });

      // 4. Counterweight Body
      ctx.fillStyle = '#ea580c';
      ctx.strokeStyle = '#c2410c';
      ctx.lineWidth = 1.3 * uiScale;
      if (ctx.roundRect) {
        ctx.beginPath();
        ctx.roundRect(-truckW / 2, rearY, truckW, chassisL, [10 * uiScale, 10 * uiScale, 3 * uiScale, 3 * uiScale]);
        ctx.fill();
        ctx.stroke();
      } else {
        ctx.fillRect(-truckW / 2, rearY, truckW, chassisL);
      }

      // Rear counterweight casting
      ctx.fillStyle = '#0f172a';
      if (ctx.roundRect) {
        ctx.beginPath();
        ctx.roundRect(-truckW / 2 + 2, rearY + 1, truckW - 4, chassisL * 0.26, [9 * uiScale, 9 * uiScale, 1, 1]);
        ctx.fill();
      }

      // 5. Overhead Guard Canopy & Seat
      const canopyW = truckW * 0.65;
      const canopyL = chassisL * 0.48;
      const canopyY = rearY + chassisL * 0.35;
      ctx.fillStyle = '#1e293b';
      ctx.strokeStyle = '#0f172a';
      ctx.lineWidth = 1 * uiScale;
      if (ctx.roundRect) {
        ctx.beginPath();
        ctx.roundRect(-canopyW / 2, canopyY, canopyW, canopyL, 3 * uiScale);
        ctx.fill();
        ctx.stroke();
      } else {
        ctx.fillRect(-canopyW / 2, canopyY, canopyW, canopyL);
      }

      // Seat
      ctx.fillStyle = '#334155';
      ctx.fillRect(-canopyW * 0.28, canopyY + canopyL * 0.15, canopyW * 0.56, canopyL * 0.42);

      // Direction arrow
      ctx.fillStyle = '#ef4444';
      ctx.beginPath();
      ctx.moveTo(0, forkTipY + 4 * uiScale);
      ctx.lineTo(-3.5 * uiScale, forkTipY - 1.5 * uiScale);
      ctx.lineTo(3.5 * uiScale, forkTipY - 1.5 * uiScale);
      ctx.closePath();
      ctx.fill();

    } else if (detectedType === 'operator') {
      // Warehouse Operator
      const matSize = Math.min(truckW, totalLen);
      const shoulderW = matSize * 0.58;
      const shoulderD = matSize * 0.32;
      const headR = shoulderD * 0.46;

      // Mat
      ctx.fillStyle = '#f59e0b';
      if (ctx.roundRect) {
        ctx.beginPath();
        ctx.roundRect(-matSize / 2, -matSize / 2, matSize, matSize, 4 * uiScale);
        ctx.fill();
      } else {
        ctx.fillRect(-matSize / 2, -matSize / 2, matSize, matSize);
      }
      ctx.fillStyle = '#334155';
      const matPad = 2 * uiScale;
      if (ctx.roundRect) {
        ctx.beginPath();
        ctx.roundRect(-matSize / 2 + matPad, -matSize / 2 + matPad, matSize - matPad * 2, matSize - matPad * 2, 3 * uiScale);
        ctx.fill();
      }

      // Boots
      ctx.fillStyle = '#0f172a';
      [-shoulderW * 0.25, shoulderW * 0.25].forEach(bx => {
        ctx.fillRect(bx - 2.5 * uiScale, -shoulderD * 0.2, 5 * uiScale, 8 * uiScale);
      });

      // Jacket
      ctx.fillStyle = '#1e293b';
      if (ctx.roundRect) {
        ctx.beginPath();
        ctx.roundRect(-shoulderW / 2, -shoulderD / 2, shoulderW, shoulderD, 4 * uiScale);
        ctx.fill();
      } else {
        ctx.fillRect(-shoulderW / 2, -shoulderD / 2, shoulderW, shoulderD);
      }

      // Orange safety vest & silver stripes
      ctx.fillStyle = '#ea580c';
      if (ctx.roundRect) {
        ctx.beginPath();
        ctx.roundRect(-shoulderW * 0.38, -shoulderD / 2, shoulderW * 0.76, shoulderD, 2 * uiScale);
        ctx.fill();
      }
      ctx.fillStyle = '#e2e8f0';
      ctx.fillRect(-shoulderW * 0.35, -shoulderD * 0.3, shoulderW * 0.70, 1.8 * uiScale);
      ctx.fillRect(-shoulderW * 0.35, shoulderD * 0.1, shoulderW * 0.70, 1.8 * uiScale);

      // Hard hat
      ctx.fillStyle = '#eab308';
      ctx.beginPath();
      ctx.arc(0, 0, Math.max(4, headR), 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = '#ca8a04';
      ctx.lineWidth = 1 * uiScale;
      ctx.stroke();

      ctx.fillStyle = '#ca8a04';
      ctx.beginPath();
      ctx.arc(0, -headR * 0.5, headR * 0.8, Math.PI, 0);
      ctx.fill();

    } else {
      // Generic 3D asset box
      const boxW = truckW * 0.85;
      const boxH = totalLen * 0.85;
      ctx.fillStyle = '#8b5cf6';
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 1.2 * uiScale;
      if (ctx.roundRect) {
        ctx.beginPath();
        ctx.roundRect(-boxW / 2, -boxH / 2, boxW, boxH, 4 * uiScale);
        ctx.fill();
        ctx.stroke();
      } else {
        ctx.fillRect(-boxW / 2, -boxH / 2, boxW, boxH);
        ctx.strokeRect(-boxW / 2, -boxH / 2, boxW, boxH);
      }
      ctx.fillStyle = '#ffffff';
      ctx.font = `bold ${Math.max(9, 10 * uiScale)}px sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('3D', 0, 0);
    }

    ctx.restore();
  };

  // ── Canvas drawing ──
  const drawMap = useCallback(() => {
    drawMapRef.current = drawMap;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    
    // Enable high-quality smoothing for downscaled raster images
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    if (!mapData.bgImageData && !mapData.svgContent) {
      // Fill background only if no layout is uploaded
      ctx.fillStyle = '#f3f4f6';
      ctx.fillRect(0, 0, 800, 600);
    }

    ctx.save();
    ctx.translate(viewTransformRef.current.x, viewTransformRef.current.y);
    ctx.scale(viewTransformRef.current.scale, viewTransformRef.current.scale);

    const uiScale = 1 / viewTransformRef.current.scale;

    if (!mapData.bgImageData && !mapData.svgContent) {
      // ── Open Floor Architectural Canvas Grid ──
      const ppm = scaleCalibration.pixelsPerMm || 0.0125;
      const meterPx = 1000 * ppm; // ~12.5px per meter
      const grid5m = 5 * meterPx; // ~62.5px per 5 meters

      // Subtle drafting grid lines
      ctx.strokeStyle = '#e2e8f0';
      ctx.lineWidth = 0.8 * uiScale;
      ctx.beginPath();
      for (let x = 0; x <= 800; x += grid5m) {
        ctx.moveTo(x, 0);
        ctx.lineTo(x, 600);
      }
      for (let y = 0; y <= 600; y += grid5m) {
        ctx.moveTo(0, y);
        ctx.lineTo(800, y);
      }
      ctx.stroke();

      // Outer drafting boundary
      ctx.strokeStyle = '#cbd5e1';
      ctx.lineWidth = 1.2 * uiScale;
      ctx.strokeRect(0, 0, 800, 600);

      // Open canvas drafting label
      ctx.fillStyle = '#94a3b8';
      ctx.font = `500 ${Math.max(10, 11 * uiScale)}px sans-serif`;
      ctx.textAlign = 'left';
      ctx.fillText(`พื้นที่โล่ง (Open Floor) • กริดทุก 5 เมตร • วาดแร็คและจัดวางโซลูชันได้ทันที`, 14 * uiScale, 586);
    }

    // ── Blank Warehouse Space Rendering ──
    if (mapData.blankWarehouse?.rect) {
      const { x, y, w, h } = mapData.blankWarehouse.rect;
      const bw = mapData.blankWarehouse;
      const ppm = scaleCalibration.pixelsPerMm || (w / (bw.widthM * 1000));

      // Floor Drop Shadow
      ctx.fillStyle = 'rgba(0, 0, 0, 0.05)';
      ctx.fillRect(x + 5 * uiScale, y + 5 * uiScale, w, h);

      // Warehouse Floor Slab
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(x, y, w, h);

      // Floor Architectural Grid (5m / 10m intervals)
      const gridM = (bw.widthM > 50 || bw.lengthM > 50) ? 10 : 5;
      const gridPx = gridM * 1000 * ppm;
      ctx.strokeStyle = '#f1f5f9';
      ctx.lineWidth = 1 * uiScale;
      ctx.beginPath();
      for (let gx = x + gridPx; gx < x + w - 1; gx += gridPx) {
        ctx.moveTo(gx, y);
        ctx.lineTo(gx, y + h);
      }
      for (let gy = y + gridPx; gy < y + h - 1; gy += gridPx) {
        ctx.moveTo(x, gy);
        ctx.lineTo(x + w, gy);
      }
      ctx.stroke();

      // Exterior Building Boundary Walls (Thick dark line)
      ctx.strokeStyle = '#1e293b';
      ctx.lineWidth = 2.5 * uiScale;
      ctx.strokeRect(x, y, w, h);

      // Corner Architectural Brackets
      const cLen = 14 * uiScale;
      ctx.strokeStyle = '#0f172a';
      ctx.lineWidth = 3.5 * uiScale;
      // Top-Left
      ctx.beginPath(); ctx.moveTo(x, y + cLen); ctx.lineTo(x, y); ctx.lineTo(x + cLen, y); ctx.stroke();
      // Top-Right
      ctx.beginPath(); ctx.moveTo(x + w - cLen, y); ctx.lineTo(x + w, y); ctx.lineTo(x + w, y + cLen); ctx.stroke();
      // Bottom-Left
      ctx.beginPath(); ctx.moveTo(x, y + h - cLen); ctx.lineTo(x, y + h); ctx.lineTo(x + cLen, y + h); ctx.stroke();
      // Bottom-Right
      ctx.beginPath(); ctx.moveTo(x + w - cLen, y + h); ctx.lineTo(x + w, y + h); ctx.lineTo(x + w, y + h - cLen); ctx.stroke();

      // Dimension Callouts (Width & Length)
      ctx.fillStyle = '#475569';
      ctx.font = `bold ${Math.max(10, 11 * uiScale)}px sans-serif`;
      ctx.textAlign = 'center';
      
      // Top dimension: ← 40.0 m →
      ctx.fillText(`←  ${bw.widthM} m (${(bw.widthM).toFixed(1)} ม.)  →`, x + w / 2, y - 8 * uiScale);

      // Left dimension (rotated 90 deg): ↕ 60.0 m
      ctx.save();
      ctx.translate(x - 12 * uiScale, y + h / 2);
      ctx.rotate(-Math.PI / 2);
      ctx.fillText(`←  ${bw.lengthM} m (${(bw.lengthM).toFixed(1)} ม.)  →`, 0, 0);
      ctx.restore();

      // Center/Bottom Information Pill
      const badgeText = `อาคารคลังสินค้า: ${bw.widthM}m × ${bw.lengthM}m (${(bw.widthM * bw.lengthM).toLocaleString()} ตร.ม.) | ความสูง ${bw.heightM}m`;
      ctx.font = `600 ${Math.max(9, 10 * uiScale)}px sans-serif`;
      const tw = ctx.measureText(badgeText).width;
      ctx.fillStyle = 'rgba(248, 250, 252, 0.95)';
      ctx.fillRect(x + w / 2 - tw / 2 - 8 * uiScale, y + h + 6 * uiScale, tw + 16 * uiScale, 18 * uiScale);
      ctx.strokeStyle = '#cbd5e1';
      ctx.lineWidth = 0.8 * uiScale;
      ctx.strokeRect(x + w / 2 - tw / 2 - 8 * uiScale, y + h + 6 * uiScale, tw + 16 * uiScale, 18 * uiScale);
      ctx.fillStyle = '#334155';
      ctx.fillText(badgeText, x + w / 2, y + h + 19 * uiScale);
    }

    // Calibration overlay
    if (isCalibratingActive) {
      ctx.save();
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.fillStyle = 'rgba(15, 23, 42, 0.06)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.restore();

      const { point1: p1, point2: p2 } = scaleCalibration;
      
      // Draw live guide line from Point A to mouse cursor
      if (p1 && !p2 && calibrationHoverPos) {
        ctx.save();
        ctx.setLineDash([6 * uiScale, 4 * uiScale]);
        ctx.strokeStyle = '#3b82f6';
        ctx.lineWidth = 2 * uiScale;
        ctx.beginPath();
        ctx.moveTo(p1.x, p1.y);
        ctx.lineTo(calibrationHoverPos.x, calibrationHoverPos.y);
        ctx.stroke();
        ctx.restore();

        const livePx = Math.hypot(calibrationHoverPos.x - p1.x, calibrationHoverPos.y - p1.y).toFixed(0);
        const hmx = (p1.x + calibrationHoverPos.x) / 2;
        const hmy = (p1.y + calibrationHoverPos.y) / 2;
        
        ctx.save();
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        const shx = hmx * viewTransformRef.current.scale + viewTransformRef.current.x;
        const shy = hmy * viewTransformRef.current.scale + viewTransformRef.current.y;
        ctx.fillStyle = 'rgba(37, 99, 235, 0.9)';
        ctx.beginPath();
        if (ctx.roundRect) ctx.roundRect(shx - 36, shy - 24, 72, 22, 6);
        else ctx.rect(shx - 36, shy - 24, 72, 22);
        ctx.fill();
        ctx.fillStyle = '#ffffff';
        ctx.font = 'bold 11px sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText(`${livePx} px`, shx, shy - 9);
        ctx.restore();
      }

      // Draw Point A and Point B pins
      [p1, p2].forEach((pt, i) => {
        if (!pt) return;
        const color = i === 0 ? '#2563eb' : '#dc2626';
        const label = i === 0 ? 'A' : 'B';
        
        // Crosshair lines
        ctx.strokeStyle = color;
        ctx.lineWidth = 2 * uiScale;
        ctx.beginPath();
        ctx.moveTo(pt.x - 18 * uiScale, pt.y);
        ctx.lineTo(pt.x + 18 * uiScale, pt.y);
        ctx.moveTo(pt.x, pt.y - 18 * uiScale);
        ctx.lineTo(pt.x, pt.y + 18 * uiScale);
        ctx.stroke();

        // Pin circle
        ctx.beginPath();
        ctx.arc(pt.x, pt.y, 8 * uiScale, 0, 2 * Math.PI);
        ctx.fillStyle = color;
        ctx.fill();
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 2.5 * uiScale;
        ctx.stroke();

        // Screen space label badge
        ctx.save();
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        const sx = pt.x * viewTransformRef.current.scale + viewTransformRef.current.x;
        const sy = pt.y * viewTransformRef.current.scale + viewTransformRef.current.y;
        
        ctx.fillStyle = color;
        ctx.beginPath();
        if (ctx.roundRect) ctx.roundRect(sx - 24, sy - 34, 48, 20, 5);
        else ctx.rect(sx - 24, sy - 34, 48, 20);
        ctx.fill();
        ctx.fillStyle = '#ffffff';
        ctx.font = 'bold 11px sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText(`จุด ${label}`, sx, sy - 20);
        ctx.restore();
      });

      // Connected line between Point A and Point B
      if (p1 && p2) {
        ctx.save();
        ctx.setLineDash([6 * uiScale, 4 * uiScale]);
        ctx.strokeStyle = '#dc2626';
        ctx.lineWidth = 2.5 * uiScale;
        ctx.beginPath();
        ctx.moveTo(p1.x, p1.y);
        ctx.lineTo(p2.x, p2.y);
        ctx.stroke();
        ctx.restore();

        const px = Math.hypot(p2.x - p1.x, p2.y - p1.y).toFixed(1);
        const mx = (p1.x + p2.x) / 2;
        const my = (p1.y + p2.y) / 2;

        ctx.save();
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        const smx = mx * viewTransformRef.current.scale + viewTransformRef.current.x;
        const smy = my * viewTransformRef.current.scale + viewTransformRef.current.y;
        
        const labelText = `${px} px`;
        ctx.font = 'bold 12px sans-serif';
        const tw = ctx.measureText(labelText).width;
        
        ctx.fillStyle = 'rgba(15, 23, 42, 0.9)';
        ctx.beginPath();
        if (ctx.roundRect) ctx.roundRect(smx - tw / 2 - 10, smy - 26, tw + 20, 24, 6);
        else ctx.rect(smx - tw / 2 - 10, smy - 26, tw + 20, 24);
        ctx.fill();
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.3)';
        ctx.lineWidth = 1;
        ctx.stroke();
        
        ctx.fillStyle = '#ffffff';
        ctx.textAlign = 'center';
        ctx.fillText(labelText, smx, smy - 10);
        ctx.restore();
      }
      return;
    }

    // ── Station areas ──
    mapData.areas.forEach(area => {
      const c = AREA_COLORS[area.type] || AREA_COLORS.storage;
      const sel = selectedArea?.id === area.id || selectedAreaIds.includes(area.id);
      if (sel) {
        ctx.fillStyle = 'rgba(59, 130, 246, 0.25)';
        ctx.fillRect(area.x, area.y, area.w, area.h);
        ctx.strokeStyle = '#2563eb';
        ctx.lineWidth = 3.0 * uiScale;
        ctx.setLineDash([]);
        ctx.strokeRect(area.x, area.y, area.w, area.h);

        const cs = Math.min(10 * uiScale, Math.min(Math.abs(area.w), Math.abs(area.h)) * 0.3);
        const ax0 = area.w < 0 ? area.x + area.w : area.x;
        const ay0 = area.h < 0 ? area.y + area.h : area.y;
        const aw = Math.abs(area.w), ah = Math.abs(area.h);
        ctx.strokeStyle = '#1d4ed8';
        ctx.lineWidth = 2.5 * uiScale;
        ctx.beginPath();
        ctx.moveTo(ax0, ay0 + cs); ctx.lineTo(ax0, ay0); ctx.lineTo(ax0 + cs, ay0);
        ctx.moveTo(ax0 + aw - cs, ay0); ctx.lineTo(ax0 + aw, ay0); ctx.lineTo(ax0 + aw, ay0 + cs);
        ctx.moveTo(ax0 + aw, ay0 + ah - cs); ctx.lineTo(ax0 + aw, ay0 + ah); ctx.lineTo(ax0 + aw - cs, ay0 + ah);
        ctx.moveTo(ax0 + cs, ay0 + ah); ctx.lineTo(ax0, ay0 + ah); ctx.lineTo(ax0, ay0 + ah - cs);
        ctx.stroke();
      } else {
        ctx.fillStyle = c.fill;
        ctx.fillRect(area.x, area.y, area.w, area.h);
        ctx.strokeStyle = c.stroke;
        ctx.lineWidth = 1.0 * uiScale;
        ctx.setLineDash([4 * uiScale, 3 * uiScale]);
        ctx.strokeRect(area.x, area.y, area.w, area.h);
        ctx.setLineDash([]);
      }
      // Pallet grid
      const ppm = scaleCalibration.pixelsPerMm;
      if (ppm) {
        const defaultPallet = mapData.palletTypes?.[0] || mapData.palletSize;
        const pw = defaultPallet.width * ppm, pl = defaultPallet.length * ppm;
        if ((area.type === 'storage' || area.type === 'storage_on_floor') && pw / uiScale > 3 && pl / uiScale > 3) {
          const cap = getPalletCapacity(area);
          const pW_px = (cap.palletW || pw) * ppm;
          const pL_px = (cap.palletL || pl) * ppm;
          const palletStatus = area.rackConfig?.palletStatus || 'full';
          const emptySlots = area.rackConfig?.emptySlots || [];
          const levels = area.rackConfig?.levels || 1;
          const emptyStack = area.rackConfig?.emptyPalletStackCount || levels;

          // Center pallets inside area if there's leftover margin
          const remW = Math.max(0, area.w - (cap.cols * pW_px));
          const remH = Math.max(0, area.h - (cap.rows * pL_px));
          const offX = remW / 2;
          const offY = remH / 2;

          for (let r = 0; r < cap.rows; r++) {
            for (let co = 0; co < cap.cols; co++) {
              const px = area.x + offX + co * pW_px;
              const py = area.y + offY + r * pL_px;
              const slotIdx = r * cap.cols + co;
              const isSlotEmpty = palletStatus === 'empty' || (palletStatus === 'custom' && emptySlots.includes(slotIdx));

              const marginPx = Math.max(1, 1.5 * uiScale);
              const drawW = pW_px - marginPx * 2;
              const drawH = pL_px - marginPx * 2;

              if (drawW > 2 && drawH > 2) {
                if (isSlotEmpty) {
                  // EMPTY PALLET on floor (พาเลทเปล่า)
                  ctx.fillStyle = 'rgba(217, 119, 6, 0.2)';
                  ctx.fillRect(px + marginPx, py + marginPx, drawW, drawH);

                  ctx.strokeStyle = '#b45309';
                  ctx.lineWidth = 1 * uiScale;
                  ctx.strokeRect(px + marginPx, py + marginPx, drawW, drawH);

                  // Deck slats & stringers
                  ctx.strokeStyle = 'rgba(180, 83, 9, 0.6)';
                  ctx.lineWidth = 0.8 * uiScale;
                  ctx.beginPath();
                  const numSlats = 4;
                  for (let s = 1; s < numSlats; s++) {
                    const slatY = py + marginPx + (drawH * s) / numSlats;
                    ctx.moveTo(px + marginPx, slatY);
                    ctx.lineTo(px + marginPx + drawW, slatY);
                  }
                  ctx.moveTo(px + marginPx + drawW * 0.18, py + marginPx);
                  ctx.lineTo(px + marginPx + drawW * 0.18, py + marginPx + drawH);
                  ctx.moveTo(px + marginPx + drawW * 0.5, py + marginPx);
                  ctx.lineTo(px + marginPx + drawW * 0.5, py + marginPx + drawH);
                  ctx.moveTo(px + marginPx + drawW * 0.82, py + marginPx);
                  ctx.lineTo(px + marginPx + drawW * 0.82, py + marginPx + drawH);
                  ctx.stroke();

                  // Text badge
                  if (drawW > 22 * uiScale && drawH > 16 * uiScale) {
                    ctx.fillStyle = '#92400e';
                    ctx.font = `bold ${Math.max(8, Math.min(11, drawH * 0.35))}px sans-serif`;
                    ctx.textAlign = 'center';
                    ctx.textBaseline = 'middle';
                    const text = emptyStack > 1 ? `x${emptyStack}` : 'EMPTY';
                    ctx.fillText(text, px + marginPx + drawW / 2, py + marginPx + drawH / 2);
                  }
                } else {
                  // FULL PALLET on floor (พาเลทเต็มสินค้า)
                  ctx.fillStyle = 'rgba(245, 158, 11, 0.35)';
                  ctx.fillRect(px + marginPx, py + marginPx, drawW, drawH);

                  ctx.strokeStyle = '#d97706';
                  ctx.lineWidth = 1 * uiScale;
                  ctx.strokeRect(px + marginPx, py + marginPx, drawW, drawH);

                  // Inner cargo box
                  const inset = Math.max(1.5, 2.5 * uiScale);
                  if (drawW > inset * 2 && drawH > inset * 2) {
                    ctx.fillStyle = 'rgba(254, 243, 199, 0.7)';
                    ctx.fillRect(px + marginPx + inset, py + marginPx + inset, drawW - inset * 2, drawH - inset * 2);
                    ctx.strokeStyle = 'rgba(217, 119, 6, 0.5)';
                    ctx.lineWidth = 0.6 * uiScale;
                    ctx.strokeRect(px + marginPx + inset, py + marginPx + inset, drawW - inset * 2, drawH - inset * 2);
                  }

                  // Level badge if stacked > 1
                  if (levels > 1 && drawW > 18 * uiScale && drawH > 14 * uiScale) {
                    ctx.fillStyle = '#b45309';
                    ctx.font = `bold ${Math.max(7, Math.min(10, drawH * 0.3))}px sans-serif`;
                    ctx.textAlign = 'center';
                    ctx.textBaseline = 'middle';
                    ctx.fillText(`${levels}L`, px + marginPx + drawW / 2, py + marginPx + drawH / 2);
                  }
                }
              }
            }
          }
        } else if ((area.type === 'pickup' || area.type === 'dropoff') && area.stationConfig?.showPallet && pw / uiScale > 3 && pl / uiScale > 3) {
          const isSlotEmpty = area.stationConfig?.palletStatus === 'empty';
          const pW_px = pw;
          const pL_px = pl;
          const px = area.x + (area.w - pW_px) / 2;
          const py = area.y + (area.h - pL_px) / 2;
          const marginPx = Math.max(1, 1.5 * uiScale);
          const drawW = pW_px - marginPx * 2;
          const drawH = pL_px - marginPx * 2;

          if (drawW > 2 && drawH > 2) {
            if (isSlotEmpty) {
              ctx.fillStyle = 'rgba(217, 119, 6, 0.2)';
              ctx.fillRect(px + marginPx, py + marginPx, drawW, drawH);
              ctx.strokeStyle = '#b45309';
              ctx.lineWidth = 1 * uiScale;
              ctx.strokeRect(px + marginPx, py + marginPx, drawW, drawH);
              ctx.strokeStyle = 'rgba(180, 83, 9, 0.6)';
              ctx.lineWidth = 0.8 * uiScale;
              ctx.beginPath();
              for (let s = 1; s < 4; s++) {
                const slatY = py + marginPx + (drawH * s) / 4;
                ctx.moveTo(px + marginPx, slatY);
                ctx.lineTo(px + marginPx + drawW, slatY);
              }
              ctx.stroke();
              if (drawW > 22 * uiScale && drawH > 16 * uiScale) {
                ctx.fillStyle = '#92400e';
                ctx.font = `bold ${Math.max(8, Math.min(11, drawH * 0.35))}px sans-serif`;
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                const emptyStack = area.stationConfig?.emptyPalletStackCount || 1;
                ctx.fillText(emptyStack > 1 ? `x${emptyStack}` : 'EMPTY', px + marginPx + drawW / 2, py + marginPx + drawH / 2);
              }
            } else {
              ctx.fillStyle = 'rgba(245, 158, 11, 0.35)';
              ctx.fillRect(px + marginPx, py + marginPx, drawW, drawH);
              ctx.strokeStyle = '#d97706';
              ctx.lineWidth = 1 * uiScale;
              ctx.strokeRect(px + marginPx, py + marginPx, drawW, drawH);
              const inset = Math.max(1.5, 2.5 * uiScale);
              if (drawW > inset * 2 && drawH > inset * 2) {
                ctx.fillStyle = 'rgba(254, 243, 199, 0.7)';
                ctx.fillRect(px + marginPx + inset, py + marginPx + inset, drawW - inset * 2, drawH - inset * 2);
                ctx.strokeStyle = 'rgba(217, 119, 6, 0.5)';
                ctx.lineWidth = 0.6 * uiScale;
                ctx.strokeRect(px + marginPx + inset, py + marginPx + inset, drawW - inset * 2, drawH - inset * 2);
              }
            }
          }
        } else if ((area.type === 'storage_2way_shuttle' || area.type === 'storage_4way_shuttle') && pw / uiScale > 1) {
          const cap = getPalletCapacity(area);
          if (cap.cols > 0 && cap.rows > 0) {
            const laneW_px = cap.laneWidth * ppm;
            const laneD_px = cap.laneDepth * ppm;
            const isVert = cap.orientation === 'vertical';
            
            for (let r = 0; r < cap.rows; r++) {
              if (cap.rTrackIndices && cap.rTrackIndices.includes(r)) continue;
              for (let c = 0; c < cap.cols; c++) {
                if (cap.cTrackIndices && cap.cTrackIndices.includes(c)) continue;
                let px, py;
                if (isVert) {
                  px = area.x + r * laneD_px + (cap.margin * ppm) / 2;
                  py = area.y + c * laneW_px + (cap.margin * ppm) / 2 + (cap.beamThick * ppm);
                  
                  ctx.fillStyle = area.type === 'storage_4way_shuttle' ? 'rgba(236,72,153,0.3)' : 'rgba(245,158,11,0.3)';
                  ctx.fillRect(px, py, pl, pw);
                  ctx.strokeStyle = area.type === 'storage_4way_shuttle' ? 'rgba(236,72,153,0.6)' : 'rgba(245,158,11,0.6)';
                  ctx.lineWidth = 0.5 * uiScale;
                  ctx.strokeRect(px, py, pl, pw);
                } else {
                  px = area.x + c * laneW_px + (cap.margin * ppm) / 2 + (cap.beamThick * ppm);
                  py = area.y + r * laneD_px + (cap.margin * ppm) / 2;
                  
                  ctx.fillStyle = area.type === 'storage_4way_shuttle' ? 'rgba(236,72,153,0.3)' : 'rgba(245,158,11,0.3)';
                  ctx.fillRect(px, py, pw, pl);
                  ctx.strokeStyle = area.type === 'storage_4way_shuttle' ? 'rgba(236,72,153,0.6)' : 'rgba(245,158,11,0.6)';
                  ctx.lineWidth = 0.5 * uiScale;
                  ctx.strokeRect(px, py, pw, pl);
                }
              }
            }
          }
        } else if (area.type === 'storage_selective_rack' && pw / uiScale > 1) {
          const cap = getPalletCapacity(area);
          const { bayWidth, beamThick, ppb, margin, orientation } = cap;
          
          if (cap.cols > 0 && cap.rows > 0) {
            const bayW_px = bayWidth * ppm;
            const beamT_px = beamThick * ppm;
            const margin_px = margin * ppm;
            const b2b_px = cap.backToBack * ppm;
            const aisle_px = cap.aisleWidth * ppm;
            const blockSize_px = (2 * pl) + b2b_px + aisle_px;
            const isVert = orientation === 'vertical';

            for (let r = 0; r < cap.rows; r++) {
              const singleRowStart = !!area.rackConfig?.singleRowStart;
              let localRow = 0;
              if (!singleRowStart) {
                const blockIndex = Math.floor(r / 2);
                const isSecond = r % 2 === 1;
                localRow = blockIndex * blockSize_px + (isSecond ? pl + b2b_px : 0);
              } else {
                if (r === 0) {
                  localRow = 0;
                } else {
                  const rAdj = r - 1;
                  const blockIndex = Math.floor(rAdj / 2);
                  const isSecond = rAdj % 2 === 1;
                  localRow = pl + aisle_px + blockIndex * blockSize_px + (isSecond ? pl + b2b_px : 0);
                }
              }
              
              // Draw columns
              for (let c = 0; c <= cap.cols; c++) {
                const localCol = c * (bayW_px + beamT_px);
                ctx.fillStyle = '#0f172a'; // Dark slate for column
                if (isVert) {
                  ctx.fillRect(area.x + localRow, area.y + localCol, pl, beamT_px);
                } else {
                  ctx.fillRect(area.x + localCol, area.y + localRow, beamT_px, pl);
                }
              }
              
              // Draw bays and pallets
              for (let c = 0; c < cap.cols; c++) {
                const localBayStart = c * (bayW_px + beamT_px) + beamT_px;
                
                // Draw beam (horizontal orange line at front and back)
                ctx.fillStyle = '#f97316'; // Orange beam
                if (isVert) {
                  ctx.fillRect(area.x + localRow, area.y + localBayStart, beamT_px, bayW_px); // Back beam
                  ctx.fillRect(area.x + localRow + pl - beamT_px, area.y + localBayStart, beamT_px, bayW_px); // Front beam
                } else {
                  ctx.fillRect(area.x + localBayStart, area.y + localRow, bayW_px, beamT_px); // Back beam
                  ctx.fillRect(area.x + localBayStart, area.y + localRow + pl - beamT_px, bayW_px, beamT_px); // Front beam
                }
                
                // Draw pallets in this bay
                for (let p = 0; p < ppb; p++) {
                  const localPalletX = localBayStart + margin_px + p * (pw + margin_px);
                  ctx.fillStyle = 'rgba(14,165,233,0.15)'; 
                  ctx.strokeStyle = 'rgba(14,165,233,0.5)'; 
                  ctx.lineWidth = 1 * uiScale;
                  
                  if (isVert) {
                    ctx.fillRect(area.x + localRow + beamT_px, area.y + localPalletX, pl - 2 * beamT_px, pw);
                    ctx.strokeRect(area.x + localRow + beamT_px, area.y + localPalletX, pl - 2 * beamT_px, pw);
                  } else {
                    ctx.fillRect(area.x + localPalletX, area.y + localRow + beamT_px, pw, pl - 2 * beamT_px);
                    ctx.strokeRect(area.x + localPalletX, area.y + localRow + beamT_px, pw, pl - 2 * beamT_px);
                  }
                }
              }
            }
          }
        } else if (area.type === 'conveyor') {
          const isHoriz = Math.abs(area.w) >= Math.abs(area.h);
          const x0 = area.w < 0 ? area.x + area.w : area.x;
          const y0 = area.h < 0 ? area.y + area.h : area.y;
          const aw = Math.abs(area.w);
          const ah = Math.abs(area.h);
          
          // Outer frame
          ctx.fillStyle = '#1e40af';
          const frameThick = Math.max(2, 3 * uiScale);
          if (isHoriz) {
            ctx.fillRect(x0, y0, aw, frameThick);
            ctx.fillRect(x0, y0 + ah - frameThick, aw, frameThick);
          } else {
            ctx.fillRect(x0, y0, frameThick, ah);
            ctx.fillRect(x0 + aw - frameThick, y0, frameThick, ah);
          }

          // Steel Rollers
          const rollerSpacing = Math.max(6 * uiScale, 10);
          ctx.strokeStyle = '#94a3b8';
          ctx.lineWidth = Math.max(1, 1.5 * uiScale);
          ctx.beginPath();
          if (isHoriz) {
            for (let rx = x0 + rollerSpacing; rx < x0 + aw - rollerSpacing / 2; rx += rollerSpacing) {
              ctx.moveTo(rx, y0 + frameThick);
              ctx.lineTo(rx, y0 + ah - frameThick);
            }
          } else {
            for (let ry = y0 + rollerSpacing; ry < y0 + ah - rollerSpacing / 2; ry += rollerSpacing) {
              ctx.moveTo(x0 + frameThick, ry);
              ctx.lineTo(x0 + aw - frameThick, ry);
            }
          }
          ctx.stroke();

          // Flow direction arrows in center
          ctx.fillStyle = '#3b82f6';
          const arrowSize = Math.max(3, 4 * uiScale);
          if (isHoriz && aw > 30) {
            const midY = y0 + ah / 2;
            for (let ax = x0 + Math.min(40, aw * 0.25); ax <= x0 + aw - Math.min(20, aw * 0.2); ax += Math.max(30, aw / 4)) {
              ctx.beginPath();
              ctx.moveTo(ax - arrowSize, midY - arrowSize);
              ctx.lineTo(ax + arrowSize, midY);
              ctx.lineTo(ax - arrowSize, midY + arrowSize);
              ctx.fill();
            }
          } else if (!isHoriz && ah > 30) {
            const midX = x0 + aw / 2;
            for (let ay = y0 + Math.min(40, ah * 0.25); ay <= y0 + ah - Math.min(20, ah * 0.2); ay += Math.max(30, ah / 4)) {
              ctx.beginPath();
              ctx.moveTo(midX - arrowSize, ay - arrowSize);
              ctx.lineTo(midX, ay + arrowSize);
              ctx.lineTo(midX + arrowSize, ay - arrowSize);
              ctx.fill();
            }
          }

          // Simulated Pallets on Conveyor (based on user-selected pallet dimensions)
          const cap = getPalletCapacity(area);
          if (ppm && cap.total > 0) {
            const convLenPx = isHoriz ? aw : ah;
            const convWidthPx = isHoriz ? ah : aw;
            const sideGapMm = area.conveyorConfig?.sideGapMm !== undefined ? area.conveyorConfig.sideGapMm : 50;
            const sideGapPx = sideGapMm * ppm;
            const palletAlongPx = (cap.palletAlongMm || 1000) * ppm;
            const maxCrossPx = Math.max(4, convWidthPx - (frameThick * 2) - (sideGapPx * 2));
            const palletCrossPx = Math.min(maxCrossPx, (cap.palletCrossMm || 1200) * ppm);
            const count = cap.total;
            const totalPalletLen = count * palletAlongPx;
            const remainingSpace = convLenPx - totalPalletLen;
            const spacingPx = Math.max(0, remainingSpace / (count + 1));

            for (let i = 0; i < count; i++) {
              const posAlong = spacingPx + i * (palletAlongPx + spacingPx);
              let px, py, pwBox, phBox;
              if (isHoriz) {
                px = x0 + posAlong;
                py = y0 + (ah - palletCrossPx) / 2;
                pwBox = palletAlongPx;
                phBox = palletCrossPx;
              } else {
                px = x0 + (aw - palletCrossPx) / 2;
                py = y0 + posAlong;
                pwBox = palletCrossPx;
                phBox = palletAlongPx;
              }

              const emptySlots = area.conveyorConfig?.emptySlots || [];
              const isEmpty = emptySlots.includes(i);

              if (pwBox > 2 && phBox > 2) {
                if (isEmpty) {
                  // EMPTY PALLET on conveyor (พาเลทเปล่า)
                  ctx.fillStyle = 'rgba(217, 119, 6, 0.2)';
                  ctx.fillRect(px, py, pwBox, phBox);

                  ctx.strokeStyle = '#b45309';
                  ctx.lineWidth = 1 * uiScale;
                  ctx.strokeRect(px, py, pwBox, phBox);

                  // Deck slats & stringers
                  ctx.strokeStyle = 'rgba(180, 83, 9, 0.6)';
                  ctx.lineWidth = 0.8 * uiScale;
                  ctx.beginPath();
                  if (isHoriz) {
                    const numSlats = 4;
                    for (let s = 1; s < numSlats; s++) {
                      const slatX = px + (pwBox * s) / numSlats;
                      ctx.moveTo(slatX, py);
                      ctx.lineTo(slatX, py + phBox);
                    }
                    ctx.moveTo(px, py + phBox * 0.2);
                    ctx.lineTo(px + pwBox, py + phBox * 0.2);
                    ctx.moveTo(px, py + phBox * 0.8);
                    ctx.lineTo(px + pwBox, py + phBox * 0.8);
                  } else {
                    const numSlats = 4;
                    for (let s = 1; s < numSlats; s++) {
                      const slatY = py + (phBox * s) / numSlats;
                      ctx.moveTo(px, slatY);
                      ctx.lineTo(px + pwBox, slatY);
                    }
                    ctx.moveTo(px + pwBox * 0.2, py);
                    ctx.lineTo(px + pwBox * 0.2, py + phBox);
                    ctx.moveTo(px + pwBox * 0.8, py);
                    ctx.lineTo(px + pwBox * 0.8, py + phBox);
                  }
                  ctx.stroke();

                  // Text badge
                  if (pwBox > 26 * uiScale && phBox > 16 * uiScale) {
                    ctx.fillStyle = '#92400e';
                    ctx.font = `bold ${Math.max(7, Math.min(10, Math.min(pwBox, phBox) * 0.28))}px sans-serif`;
                    ctx.textAlign = 'center';
                    ctx.textBaseline = 'middle';
                    ctx.fillText('EMPTY', px + pwBox / 2, py + phBox / 2);
                  }
                } else {
                  // FULL PALLET on conveyor (พาเลทเต็มสินค้า)
                  ctx.fillStyle = 'rgba(251, 191, 36, 0.45)';
                  ctx.fillRect(px, py, pwBox, phBox);

                  ctx.strokeStyle = '#d97706';
                  ctx.lineWidth = 1 * uiScale;
                  ctx.strokeRect(px, py, pwBox, phBox);

                  // Inner cargo box
                  const inset = Math.max(1, 2 * uiScale);
                  if (pwBox > inset * 2 && phBox > inset * 2) {
                    ctx.fillStyle = 'rgba(245, 158, 11, 0.25)';
                    ctx.fillRect(px + inset, py + inset, pwBox - inset * 2, phBox - inset * 2);
                  }

                  // Pallet deck slats / wooden boards
                  ctx.strokeStyle = 'rgba(180, 83, 9, 0.5)';
                  ctx.lineWidth = 0.8 * uiScale;
                  ctx.beginPath();
                  if (isHoriz) {
                    ctx.moveTo(px, py + phBox / 2);
                    ctx.lineTo(px + pwBox, py + phBox / 2);
                    if (pwBox > 14 * uiScale) {
                      ctx.moveTo(px + pwBox * 0.33, py);
                      ctx.lineTo(px + pwBox * 0.33, py + phBox);
                      ctx.moveTo(px + pwBox * 0.66, py);
                      ctx.lineTo(px + pwBox * 0.66, py + phBox);
                    }
                  } else {
                    ctx.moveTo(px + pwBox / 2, py);
                    ctx.lineTo(px + pwBox / 2, py + phBox);
                    if (phBox > 14 * uiScale) {
                      ctx.moveTo(px, py + phBox * 0.33);
                      ctx.lineTo(px + pwBox, py + phBox * 0.33);
                      ctx.moveTo(px, py + phBox * 0.66);
                      ctx.lineTo(px + pwBox, py + phBox * 0.66);
                    }
                  }
                  ctx.stroke();
                }
              }
            }
          }

          // Render Simulated Operators beside Conveyor
          if (area.conveyorConfig?.operatorsEnabled && ppm) {
            const side = area.conveyorConfig?.operatorSide || 'right';
            const countPerSide = area.conveyorConfig?.operatorCount || 1;
            const role = area.conveyorConfig?.operatorRole || 'picking';
            const sides = side === 'both' ? ['left', 'right'] : [side];

            const convLen = isHoriz ? aw : ah;
            const convWidth = isHoriz ? ah : aw;

            const shoulderW = 460 * ppm;
            const shoulderD = 260 * ppm;
            const workerRadius = 140 * ppm;
            const distFromTrack = 180 * ppm;

            sides.forEach(s => {
              const workerSpacing = convLen / (countPerSide + 1);

              for (let wi = 0; wi < countPerSide; wi++) {
                const posAlong = workerSpacing * (wi + 1);
                let wx, wy;

                if (isHoriz) {
                  wx = x0 + posAlong;
                  wy = s === 'left' ? (y0 - distFromTrack - shoulderD / 2) : (y0 + ah + distFromTrack + shoulderD / 2);
                } else {
                  wx = s === 'left' ? (x0 - distFromTrack - shoulderD / 2) : (x0 + aw + distFromTrack + shoulderD / 2);
                  wy = y0 + posAlong;
                }

                // Standing safety mat on floor
                const matW = isHoriz ? Math.max(shoulderW * 1.3, 600 * ppm) : Math.max(shoulderD * 1.6, 450 * ppm);
                const matH = isHoriz ? Math.max(shoulderD * 1.6, 450 * ppm) : Math.max(shoulderW * 1.3, 600 * ppm);
                ctx.fillStyle = 'rgba(51, 65, 85, 0.2)';
                ctx.fillRect(wx - matW / 2, wy - matH / 2, matW, matH);
                ctx.strokeStyle = '#f59e0b';
                ctx.lineWidth = 1 * uiScale;
                ctx.setLineDash([3 * uiScale, 2 * uiScale]);
                ctx.strokeRect(wx - matW / 2, wy - matH / 2, matW, matH);
                ctx.setLineDash([]);

                // Worker shoulders (Safety orange vest)
                ctx.save();
                ctx.translate(wx, wy);

                if (isHoriz) {
                  ctx.fillStyle = '#ea580c';
                  if (ctx.roundRect) ctx.roundRect(-shoulderW / 2, -shoulderD / 2, shoulderW, shoulderD, 3 * uiScale);
                  else ctx.fillRect(-shoulderW / 2, -shoulderD / 2, shoulderW, shoulderD);
                  ctx.fill();
                  ctx.strokeStyle = '#c2410c';
                  ctx.lineWidth = 1 * uiScale;
                  ctx.stroke();

                  // Reflective silver bands
                  ctx.fillStyle = '#f8fafc';
                  ctx.fillRect(-shoulderW / 2 + 2 * uiScale, -shoulderD / 2 + 2 * uiScale, shoulderW - 4 * uiScale, 2 * uiScale);
                  ctx.fillRect(-shoulderW / 2 + 2 * uiScale, shoulderD / 2 - 4 * uiScale, shoulderW - 4 * uiScale, 2 * uiScale);
                } else {
                  ctx.fillStyle = '#ea580c';
                  if (ctx.roundRect) ctx.roundRect(-shoulderD / 2, -shoulderW / 2, shoulderD, shoulderW, 3 * uiScale);
                  else ctx.fillRect(-shoulderD / 2, -shoulderW / 2, shoulderD, shoulderW);
                  ctx.fill();
                  ctx.strokeStyle = '#c2410c';
                  ctx.lineWidth = 1 * uiScale;
                  ctx.stroke();

                  // Reflective silver bands
                  ctx.fillStyle = '#f8fafc';
                  ctx.fillRect(-shoulderD / 2 + 2 * uiScale, -shoulderW / 2 + 2 * uiScale, 2 * uiScale, shoulderW - 4 * uiScale);
                  ctx.fillRect(shoulderD / 2 - 4 * uiScale, -shoulderW / 2 + 2 * uiScale, 2 * uiScale, shoulderW - 4 * uiScale);
                }

                // Head with yellow hard hat
                ctx.fillStyle = '#eab308';
                ctx.beginPath();
                ctx.arc(0, 0, Math.max(3, workerRadius), 0, Math.PI * 2);
                ctx.fill();
                ctx.strokeStyle = '#ca8a04';
                ctx.lineWidth = 1 * uiScale;
                ctx.stroke();

                ctx.restore();

                // Role badge
                if (shoulderW > 16 * uiScale) {
                  ctx.fillStyle = '#9a3412';
                  ctx.font = `bold ${Math.max(8, 9 * uiScale)}px sans-serif`;
                  ctx.textAlign = 'center';
                  ctx.textBaseline = 'middle';
                  ctx.fillText(
                    role === 'picking' ? 'Picking' : role === 'packing' ? 'Packing' : role === 'qc' ? 'QC' : 'Operator',
                    wx,
                    wy + (isHoriz ? (s === 'left' ? -shoulderD / 2 - 6 * uiScale : shoulderD / 2 + 8 * uiScale) : shoulderW / 2 + 8 * uiScale)
                  );
                }
              }
            });
          }
        } else if (area.type === 'operator_station') {
          const x0 = area.w < 0 ? area.x + area.w : area.x;
          const y0 = area.h < 0 ? area.y + area.h : area.y;
          const aw = Math.abs(area.w);
          const ah = Math.abs(area.h);

          // Floor safety boundary
          ctx.fillStyle = 'rgba(16, 185, 129, 0.12)';
          ctx.fillRect(x0, y0, aw, ah);
          ctx.strokeStyle = '#059669';
          ctx.lineWidth = 2 * uiScale;
          ctx.strokeRect(x0, y0, aw, ah);

          // Diagonal safety stripes along border
          ctx.strokeStyle = '#f59e0b';
          ctx.lineWidth = 1 * uiScale;
          ctx.setLineDash([6 * uiScale, 4 * uiScale]);
          ctx.strokeRect(x0 + 2 * uiScale, y0 + 2 * uiScale, aw - 4 * uiScale, ah - 4 * uiScale);
          ctx.setLineDash([]);

          // Operator in center of station
          const cx = x0 + aw / 2;
          const cy = y0 + ah / 2;
          const shoulderW = Math.min(aw * 0.5, 460 * (ppm || 0.0125));
          const shoulderD = Math.min(ah * 0.35, 260 * (ppm || 0.0125));
          const workerR = Math.min(shoulderD * 0.45, 120 * (ppm || 0.0125));

          ctx.fillStyle = '#ea580c';
          if (ctx.roundRect) ctx.roundRect(cx - shoulderW / 2, cy - shoulderD / 2, shoulderW, shoulderD, 3 * uiScale);
          else ctx.fillRect(cx - shoulderW / 2, cy - shoulderD / 2, shoulderW, shoulderD);
          ctx.fill();

          ctx.fillStyle = '#eab308';
          ctx.beginPath();
          ctx.arc(cx, cy, Math.max(3, workerR), 0, Math.PI * 2);
          ctx.fill();

        } else if (area.type === 'model_3d') {
          const x0 = area.w < 0 ? area.x + area.w : area.x;
          const y0 = area.h < 0 ? area.y + area.h : area.y;
          const aw = Math.abs(area.w);
          const ah = Math.abs(area.h);
          const rotDeg = area.modelConfig?.rotationDeg ?? 0;

          // Rounded 3D asset boundary floor zone with soft purple tint (or bold bright blue if selected!)
          ctx.fillStyle = sel ? 'rgba(59, 130, 246, 0.28)' : 'rgba(139, 92, 246, 0.10)';
          ctx.strokeStyle = sel ? '#2563eb' : '#8b5cf6';
          ctx.lineWidth = (sel ? 3.0 : 1.6) * uiScale;
          if (ctx.roundRect) {
            ctx.beginPath();
            ctx.roundRect(x0, y0, aw, ah, 5 * uiScale);
            ctx.fill();
            ctx.stroke();
          } else {
            ctx.fillRect(x0, y0, aw, ah);
            ctx.strokeRect(x0, y0, aw, ah);
          }

          // If selected, draw corner bracket accents
          if (sel) {
            const cs = Math.min(10 * uiScale, Math.min(aw, ah) * 0.35);
            ctx.strokeStyle = '#1d4ed8';
            ctx.lineWidth = 2.8 * uiScale;
            ctx.beginPath();
            // NW
            ctx.moveTo(x0, y0 + cs); ctx.lineTo(x0, y0); ctx.lineTo(x0 + cs, y0);
            // NE
            ctx.moveTo(x0 + aw - cs, y0); ctx.lineTo(x0 + aw, y0); ctx.lineTo(x0 + aw, y0 + cs);
            // SE
            ctx.moveTo(x0 + aw, y0 + ah - cs); ctx.lineTo(x0 + aw, y0 + ah); ctx.lineTo(x0 + aw - cs, y0 + ah);
            // SW
            ctx.moveTo(x0 + cs, y0 + ah); ctx.lineTo(x0, y0 + ah); ctx.lineTo(x0, y0 + ah - cs);
            ctx.stroke();
          }

          // Center coordinate
          const cx = x0 + aw / 2;
          const cy = y0 + ah / 2;

          draw2DModelGraphic(ctx, cx, cy, aw, ah, rotDeg, area.modelConfig, ppm, uiScale);
        }
      }
      // Draw resize handles if selected
      if (sel && mode === 'select' && selectedAreaIds.length <= 1) {
        const hs = 8 * uiScale;
        ctx.fillStyle = '#fff';
        ctx.strokeStyle = '#3b82f6';
        ctx.lineWidth = 1.5 * uiScale;
        const drawHandle = (hx, hy) => {
          ctx.beginPath();
          ctx.rect(hx - hs/2, hy - hs/2, hs, hs);
          ctx.fill();
          ctx.stroke();
        };
        drawHandle(area.x, area.y);
        drawHandle(area.x + area.w, area.y);
        drawHandle(area.x, area.y + area.h);
        drawHandle(area.x + area.w, area.y + area.h);
      }

      // ── Clean, Compact Area Pill Badge ──
      const ax = Math.min(area.x, area.x + area.w);
      const ay = Math.min(area.y, area.y + area.h);
      const cap = getPalletCapacity(area);
      const opCount = area.conveyorConfig?.operatorsEnabled 
        ? ((area.conveyorConfig?.operatorSide === 'both' ? 2 : 1) * (area.conveyorConfig?.operatorCount || 1))
        : 0;
      const opText = opCount > 0 ? `, ${opCount} ops` : '';
      const capText = cap.total > 0 ? ` (${cap.total} pallets${opText})` : (opText ? ` (${opCount} ops)` : '');

      let mainTitle = '';
      if (area.type === 'model_3d') {
        const modelName = area.modelConfig?.modelName || (area.label && area.label !== '3D Asset' ? area.label : '3D Asset');
        const rotDeg = area.modelConfig?.rotationDeg ?? 0;
        mainTitle = `${modelName} (${rotDeg}°)`;
      } else if (area.type === 'conveyor') {
        const isDefault = !area.label || area.label === 'Conveyor' || area.label === 'Conveyor Line';
        mainTitle = isDefault ? 'Conveyor' : area.label;
      } else if (area.type === 'operator_station') {
        const isDefault = !area.label || area.label === 'Workstation' || area.label === 'Operator Station';
        mainTitle = isDefault ? 'Workstation' : area.label;
      } else {
        const isDefault = !area.label || area.label === c.label || area.label === 'Storage Area';
        mainTitle = isDefault ? c.label : area.label;
      }

      const labelText = `${mainTitle}${capText}`;
      const fontSize = 8.5 * uiScale;

      ctx.save();
      ctx.font = `600 ${fontSize}px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`;
      ctx.textBaseline = 'middle';
      ctx.textAlign = 'left';

      const tw = ctx.measureText(labelText).width;
      const padX = 4 * uiScale;
      const dotR = 2 * uiScale;
      const dotSpace = dotR * 2 + 3 * uiScale;
      const badgeH = 13.5 * uiScale;
      const badgeW = tw + padX * 2 + dotSpace;

      // Position badge right above area boundary; if near top edge of canvas, place inside
      let badgeX = ax;
      let badgeY = ay - badgeH - 2 * uiScale;
      if (badgeY < 2 * uiScale) {
        badgeY = ay + 2 * uiScale;
      }

      // Compact sleek pill background
      ctx.fillStyle = sel ? 'rgba(15, 23, 42, 0.92)' : 'rgba(15, 23, 42, 0.80)';
      ctx.strokeStyle = sel ? '#38bdf8' : (c.stroke || 'rgba(255, 255, 255, 0.25)');
      ctx.lineWidth = (sel ? 1.2 : 0.8) * uiScale;
      ctx.beginPath();
      if (ctx.roundRect) {
        ctx.roundRect(badgeX, badgeY, badgeW, badgeH, 3 * uiScale);
      } else {
        ctx.rect(badgeX, badgeY, badgeW, badgeH);
      }
      ctx.fill();
      ctx.stroke();

      // Tiny colored category indicator dot inside the pill
      ctx.fillStyle = c.stroke || '#94a3b8';
      ctx.beginPath();
      ctx.arc(badgeX + padX + dotR, badgeY + badgeH / 2, dotR, 0, Math.PI * 2);
      ctx.fill();

      // Clean white text
      ctx.fillStyle = '#ffffff';
      ctx.fillText(labelText, badgeX + padX + dotSpace, badgeY + badgeH / 2);
      ctx.restore();
    });

    // ── Corridors ──
    (mapData.corridors || []).forEach(cor => {
      const sel = selectedCorridor?.id === cor.id || selectedCorridorIds.includes(cor.id);
      ctx.fillStyle = sel ? 'rgba(59, 130, 246, 0.22)' : CORRIDOR_COLOR.fill;
      ctx.fillRect(cor.x, cor.y, cor.w, cor.h);
      ctx.strokeStyle = sel ? '#2563eb' : 'rgba(245, 158, 11, 0.5)';
      ctx.lineWidth = (sel ? 3.0 : 1.5) * uiScale;
      ctx.strokeRect(cor.x, cor.y, cor.w, cor.h);
      
      // Draw resize handles if selected
      if (sel && mode === 'select' && selectedCorridorIds.length <= 1) {
        const hs = 8 * uiScale;
        ctx.fillStyle = '#fff';
        ctx.strokeStyle = '#f59e0b';
        ctx.lineWidth = 1.5 * uiScale;
        const drawHandle = (hx, hy) => {
          ctx.beginPath();
          ctx.rect(hx - hs/2, hy - hs/2, hs, hs);
          ctx.fill();
          ctx.stroke();
        };
        drawHandle(cor.x, cor.y);
        drawHandle(cor.x + cor.w, cor.y);
        drawHandle(cor.x, cor.y + cor.h);
        drawHandle(cor.x + cor.w, cor.y + cor.h);
      }
      
      // ── Clean, Compact Corridor Pill Badge ──
      const cx0 = Math.min(cor.x, cor.x + cor.w);
      const cy0 = Math.min(cor.y, cor.y + cor.h);
      const corLabel = cor.label || 'Corridor';

      ctx.save();
      const corFontSize = 8 * uiScale;
      ctx.font = `600 ${corFontSize}px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`;
      ctx.textBaseline = 'middle';
      ctx.textAlign = 'left';

      const ctw = ctx.measureText(corLabel).width;
      const cPadX = 4 * uiScale;
      const cBadgeH = 12.5 * uiScale;
      const cBadgeW = ctw + cPadX * 2;
      let cBadgeY = cy0 - cBadgeH - 2 * uiScale;
      if (cBadgeY < 2 * uiScale) cBadgeY = cy0 + 2 * uiScale;

      ctx.fillStyle = sel ? 'rgba(15, 23, 42, 0.92)' : 'rgba(30, 41, 59, 0.80)';
      ctx.strokeStyle = sel ? '#f59e0b' : 'rgba(245, 158, 11, 0.6)';
      ctx.lineWidth = (sel ? 1.2 : 0.8) * uiScale;
      ctx.beginPath();
      if (ctx.roundRect) {
        ctx.roundRect(cx0, cBadgeY, cBadgeW, cBadgeH, 3 * uiScale);
      } else {
        ctx.rect(cx0, cBadgeY, cBadgeW, cBadgeH);
      }
      ctx.fill();
      ctx.stroke();

      ctx.fillStyle = '#fbbf24';
      ctx.fillText(corLabel, cx0 + cPadX, cBadgeY + cBadgeH / 2);
      ctx.restore();
    });

    // Drawing preview
    if (drawingRect) {
      const dx = Math.min(drawingRect.startX, drawingRect.curX), dy = Math.min(drawingRect.startY, drawingRect.curY);
      const dw = Math.abs(drawingRect.curX - drawingRect.startX), dh = Math.abs(drawingRect.curY - drawingRect.startY);
      const isCorr = mode === 'draw_corridor';
      const isZoom = mode === 'zoom_window';
      const isArea = mode === 'draw_area';
      ctx.strokeStyle = isZoom ? '#2563eb' : (isCorr ? '#f59e0b' : '#8b5cf6'); ctx.lineWidth = (isZoom ? 1 : 1.5) * uiScale; ctx.setLineDash([6 * uiScale, 3 * uiScale]);
      ctx.strokeRect(dx, dy, dw, dh); ctx.setLineDash([]);
      ctx.fillStyle = isZoom ? 'rgba(37,99,235,0.1)' : (isCorr ? 'rgba(251,191,36,0.12)' : 'rgba(139,92,246,0.1)');
      ctx.fillRect(dx, dy, dw, dh);
      
      if (scaleCalibration.pixelsPerMm && !isZoom) {
        let lbl = `${(dw / scaleCalibration.pixelsPerMm).toFixed(0)} × ${(dh / scaleCalibration.pixelsPerMm).toFixed(0)} mm`;
        
        if (isArea) {
          const isConveyor = activeRackType === 'conveyor';
          const is3DModel = activeRackType === 'model_3d';
          const cap = getPalletCapacity({ w: dw, h: dh, type: activeRackType });
          if (is3DModel) {
            const first3D = available3DModelsRef.current?.[0];
            const meterW = (dw / scaleCalibration.pixelsPerMm / 1000).toFixed(1);
            const meterH = (dh / scaleCalibration.pixelsPerMm / 1000).toFixed(1);
            lbl = `${meterW} × ${meterH} m (${first3D?.name || '3D Model'})`;
            if (dw > 8 && dh > 8) {
              draw2DModelGraphic(ctx, dx + dw / 2, dy + dh / 2, dw, dh, 0, first3D, scaleCalibration.pixelsPerMm, uiScale);
            }
          } else {
            lbl += ` (${cap.total} pallets)`;
          }
          
          if (isConveyor && dw > 5 && dh > 5) {
            const isHoriz = dw >= dh;
            const frameThick = Math.max(2, 3 * uiScale);
            // Outer rails preview
            ctx.fillStyle = '#1e40af';
            if (isHoriz) {
              ctx.fillRect(dx, dy, dw, frameThick);
              ctx.fillRect(dx, dy + dh - frameThick, dw, frameThick);
            } else {
              ctx.fillRect(dx, dy, frameThick, dh);
              ctx.fillRect(dx + dw - frameThick, dy, frameThick, dh);
            }

            // Rollers preview
            const rollerSpacing = Math.max(6 * uiScale, 10);
            ctx.strokeStyle = '#94a3b8';
            ctx.lineWidth = Math.max(1, 1.5 * uiScale);
            ctx.beginPath();
            if (isHoriz) {
              for (let rx = dx + rollerSpacing; rx < dx + dw - rollerSpacing / 2; rx += rollerSpacing) {
                ctx.moveTo(rx, dy + frameThick);
                ctx.lineTo(rx, dy + dh - frameThick);
              }
            } else {
              for (let ry = dy + rollerSpacing; ry < dy + dh - rollerSpacing / 2; ry += rollerSpacing) {
                ctx.moveTo(dx + frameThick, ry);
                ctx.lineTo(dx + dw - frameThick, ry);
              }
            }
            ctx.stroke();

            // Flow arrows preview
            ctx.fillStyle = '#3b82f6';
            const arrowSize = Math.max(3, 4 * uiScale);
            if (isHoriz && dw > 30) {
              const midY = dy + dh / 2;
              for (let ax = dx + Math.min(40, dw * 0.25); ax <= dx + dw - Math.min(20, dw * 0.2); ax += Math.max(30, dw / 4)) {
                ctx.beginPath();
                ctx.moveTo(ax - arrowSize, midY - arrowSize);
                ctx.lineTo(ax + arrowSize, midY);
                ctx.lineTo(ax - arrowSize, midY + arrowSize);
                ctx.fill();
              }
            } else if (!isHoriz && dh > 30) {
              const midX = dx + dw / 2;
              for (let ay = dy + Math.min(40, dh * 0.25); ay <= dy + dh - Math.min(20, dh * 0.2); ay += Math.max(30, dh / 4)) {
                ctx.beginPath();
                ctx.moveTo(midX - arrowSize, ay - arrowSize);
                ctx.lineTo(midX, ay + arrowSize);
                ctx.lineTo(midX + arrowSize, ay - arrowSize);
                ctx.fill();
              }
            }

            // Pallets preview on conveyor
            if (cap.total > 0 && scaleCalibration.pixelsPerMm) {
              const ppm = scaleCalibration.pixelsPerMm;
              const convLenPx = isHoriz ? dw : dh;
              const convWidthPx = isHoriz ? dh : dw;
              const sideGapMm = 50;
              const sideGapPx = sideGapMm * ppm;
              const palletAlongPx = (cap.palletAlongMm || 1000) * ppm;
              const maxCrossPx = Math.max(4, convWidthPx - (frameThick * 2) - (sideGapPx * 2));
              const palletCrossPx = Math.min(maxCrossPx, (cap.palletCrossMm || 1200) * ppm);
              const count = cap.total;
              const totalPalletLen = count * palletAlongPx;
              const remainingSpace = convLenPx - totalPalletLen;
              const spacingPx = Math.max(0, remainingSpace / (count + 1));
              
              for (let i = 0; i < count; i++) {
                const posAlong = spacingPx + i * (palletAlongPx + spacingPx);
                let px, py, pwBox, phBox;
                if (isHoriz) {
                  px = dx + posAlong;
                  py = dy + (dh - palletCrossPx) / 2;
                  pwBox = palletAlongPx;
                  phBox = palletCrossPx;
                } else {
                  px = dx + (dw - palletCrossPx) / 2;
                  py = dy + posAlong;
                  pwBox = palletCrossPx;
                  phBox = palletAlongPx;
                }
                if (pwBox > 2 && phBox > 2) {
                  ctx.fillStyle = 'rgba(251, 191, 36, 0.55)';
                  ctx.fillRect(px, py, pwBox, phBox);
                  ctx.strokeStyle = '#d97706';
                  ctx.lineWidth = 1 * uiScale;
                  ctx.strokeRect(px, py, pwBox, phBox);
                  
                  ctx.strokeStyle = 'rgba(180, 83, 9, 0.5)';
                  ctx.lineWidth = 0.8 * uiScale;
                  ctx.beginPath();
                  if (isHoriz) {
                    ctx.moveTo(px, py + phBox / 2); ctx.lineTo(px + pwBox, py + phBox / 2);
                  } else {
                    ctx.moveTo(px + pwBox / 2, py); ctx.lineTo(px + pwBox / 2, py + phBox);
                  }
                  ctx.stroke();
                }
              }
            }
          } else if (cap.total > 0) {
            const ppm = scaleCalibration.pixelsPerMm;
            const defaultPallet = mapData.palletTypes?.[0] || mapData.palletSize;
            const pw = defaultPallet.width * ppm;
            const pl = defaultPallet.length * ppm;
            for (let r = 0; r < cap.rows; r++) {
              for (let co = 0; co < cap.cols; co++) {
                ctx.fillStyle = 'rgba(139,92,246,0.3)';
                ctx.fillRect(dx + co * pw + 1 * uiScale, dy + r * pl + 1 * uiScale, pw - 2 * uiScale, pl - 2 * uiScale);
              }
            }
          }
        }
        
        ctx.save();
        ctx.setTransform(1, 0, 0, 1, 0, 0); // Reset to screen space
        
        ctx.font = 'bold 11px sans-serif'; 
        const tw = ctx.measureText(lbl).width;
        
        // Position tooltip OUTSIDE the bounding box so it never obscures the preview graphics!
        const topScreenY = dy * viewTransformRef.current.scale + viewTransformRef.current.y;
        const bottomScreenY = (dy + dh) * viewTransformRef.current.scale + viewTransformRef.current.y;
        const centerScreenX = (dx + dw / 2) * viewTransformRef.current.scale + viewTransformRef.current.x;

        let badgeY = topScreenY - 18;
        if (badgeY < 24) {
          badgeY = bottomScreenY + 18;
        }
        
        // Clamp X so the badge doesn't overflow canvas bounds
        const badgeX = Math.max(tw / 2 + 12, Math.min(canvas.width - tw / 2 - 12, centerScreenX));

        ctx.fillStyle = 'rgba(15, 23, 42, 0.88)'; 
        const bx = badgeX - tw / 2 - 8;
        const by = badgeY - 11;
        const bw = tw + 16;
        const bh = 22;
        
        ctx.beginPath();
        if (ctx.roundRect) ctx.roundRect(bx, by, bw, bh, 5);
        else ctx.rect(bx, by, bw, bh);
        ctx.fill();
        
        ctx.fillStyle = '#ffffff'; 
        ctx.textAlign = 'center'; 
        ctx.textBaseline = 'middle';
        ctx.fillText(lbl, badgeX, badgeY);
        ctx.restore();
      }
    }

    if (measuringLine && mode === 'measure' && scaleCalibration.pixelsPerMm) {
      const { start, end } = measuringLine;
      const distPx = Math.hypot(end.x - start.x, end.y - start.y);
      const distMm = distPx / scaleCalibration.pixelsPerMm;

      ctx.beginPath();
      ctx.moveTo(start.x, start.y);
      ctx.lineTo(end.x, end.y);
      ctx.strokeStyle = '#6366f1';
      ctx.lineWidth = 2 * uiScale;
      ctx.setLineDash([4 * uiScale, 4 * uiScale]);
      ctx.stroke();
      ctx.setLineDash([]);

      const midX = start.x + (end.x - start.x) / 2;
      const midY = start.y + (end.y - start.y) / 2;
      const lbl = `${distMm.toFixed(0)} mm`;
      
      ctx.save();
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      
      ctx.font = `bold 12px sans-serif`;
      const tw = ctx.measureText(lbl).width;
      
      const screenX = midX * viewTransformRef.current.scale + viewTransformRef.current.x;
      const screenY = midY * viewTransformRef.current.scale + viewTransformRef.current.y;
      
      ctx.fillStyle = 'rgba(0,0,0,0.8)';
      ctx.fillRect(screenX - tw / 2 - 6, screenY - 14, tw + 12, 24);
      ctx.fillStyle = '#fff';
      ctx.textAlign = 'center';
      ctx.fillText(lbl, screenX, screenY + 4);
      
      ctx.restore();
    }


    // ── Auto-generated edges (lane centerlines) ──
    ctx.strokeStyle = 'rgba(100, 100, 100, 0.4)';
    ctx.lineWidth = 0.2 * uiScale;
    ctx.setLineDash([5 * uiScale, 5 * uiScale]);
    (mapData.edges || []).forEach(edge => {
      const from = mapData.nodes.find(n => n.id === edge.from);
      const to = mapData.nodes.find(n => n.id === edge.to);
      if (from && to) {
        ctx.beginPath();
        ctx.moveTo(from.x, from.y);
        ctx.lineTo(to.x, to.y);
        ctx.stroke();
        
        // Draw direction arrow if one-way
        if (edge.dir === 'uni') {
           const midX = (from.x + to.x) / 2;
           const midY = (from.y + to.y) / 2;
           const angle = Math.atan2(to.y - from.y, to.x - from.x);
           ctx.save();
           ctx.translate(midX, midY);
           ctx.rotate(angle);
           ctx.beginPath();
           ctx.moveTo(0, 0);
           ctx.lineTo(-4 * uiScale, -3 * uiScale);
           ctx.lineTo(-4 * uiScale, 3 * uiScale);
           ctx.closePath();
           ctx.fillStyle = 'rgba(100, 100, 100, 0.6)';
           ctx.fill();
           ctx.restore();
        }
      }
    });
    ctx.setLineDash([]);

    // ── Auto-generated intersection nodes ──
    (mapData.nodes || []).filter(n => n.type === 'intersection').forEach(n => {
      ctx.beginPath();
      ctx.arc(n.x, n.y, 1.2 * uiScale, 0, 2 * Math.PI);
      ctx.fillStyle = '#f59e0b';
      ctx.fill();
      ctx.strokeStyle = '#fff';
      ctx.lineWidth = 0.4 * uiScale;
      ctx.stroke();
    });

    // ── Material Flows (Arrows) ──
    if (activeStep === 6) {
      (mapData.flows || []).forEach(flow => {
        const sources = flow.sourceAreaIds || (flow.sourceAreaId ? [flow.sourceAreaId] : []);
        const targets = flow.targetAreaIds || (flow.targetAreaId ? [flow.targetAreaId] : []);
        
        sources.forEach(sourceId => {
          targets.forEach(targetId => {
            const src = mapData.areas.find(a => a.id === sourceId);
            const tgt = mapData.areas.find(a => a.id === targetId);
            
            if (src && tgt) {
              const sx = src.x + src.w / 2;
              const sy = src.y + src.h / 2;
              const tx = tgt.x + tgt.w / 2;
              const ty = tgt.y + tgt.h / 2;
              const angle = Math.atan2(ty - sy, tx - sx);
              
              ctx.beginPath();
              ctx.moveTo(sx, sy);
              ctx.lineTo(tx, ty);
              ctx.strokeStyle = 'rgba(59, 130, 246, 0.8)';
              ctx.lineWidth = 0.8 * uiScale;
              ctx.setLineDash([8 * uiScale, 6 * uiScale]);
              ctx.stroke();
              ctx.setLineDash([]);
              
              // Arrowhead
              ctx.beginPath();
              ctx.moveTo(tx, ty);
              ctx.lineTo(tx - 15 * uiScale * Math.cos(angle - Math.PI / 6), ty - 15 * uiScale * Math.sin(angle - Math.PI / 6));
              ctx.lineTo(tx - 15 * uiScale * Math.cos(angle + Math.PI / 6), ty - 15 * uiScale * Math.sin(angle + Math.PI / 6));
              ctx.lineTo(tx, ty);
              ctx.fillStyle = 'rgba(59, 130, 246, 0.8)';
              ctx.fill();
            }
          });
        });
      });
      
      if (selectingForFlow) {
        const isSrc = selectingForFlow.type === 'source';
        const prompt = `Click an area to set the ${isSrc ? 'From (Pickup/Storage)' : 'To (Drop-off/Storage)'} location`;
        
        ctx.save();
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.font = 'bold 14px sans-serif';
        const tw = ctx.measureText(prompt).width;
        ctx.fillStyle = 'rgba(59, 130, 246, 0.9)';
        ctx.fillRect(canvas.width / 2 - tw / 2 - 20, 20, tw + 40, 36);
        ctx.fillStyle = '#fff';
        ctx.textAlign = 'center';
        ctx.fillText(prompt, canvas.width / 2, 43);
        ctx.restore();
      }
    }

    // Multi-select marquee selection box
    if (selectionBox) {
      const sx = Math.min(selectionBox.startX, selectionBox.curX);
      const sy = Math.min(selectionBox.startY, selectionBox.curY);
      const sw = Math.abs(selectionBox.curX - selectionBox.startX);
      const sh = Math.abs(selectionBox.curY - selectionBox.startY);

      ctx.save();
      ctx.strokeStyle = '#2563eb';
      ctx.lineWidth = 1.5 * uiScale;
      ctx.setLineDash([5 * uiScale, 3 * uiScale]);
      ctx.strokeRect(sx, sy, sw, sh);
      ctx.fillStyle = 'rgba(37, 99, 235, 0.15)';
      ctx.fillRect(sx, sy, sw, sh);
      ctx.restore();
    }

    ctx.restore(); // Final restore to balance the save at the top

  }, [bgImage, mapData, selectedNode, selectedArea, selectedAreaIds, selectedCorridor, selectedCorridorIds, scaleCalibration, activeStep, drawingRect, selectionBox, mode, getPalletCapacity, selectingForFlow, mapData.bgSettings, measuringLine, isCalibratingActive, calibrationHoverPos]);



  useEffect(() => {
    drawMap();
  }, [drawMap]);

  const applyViewTransform = useCallback(() => {
    if (bgImageRef.current) {
      bgImageRef.current.style.transform = `translate(${(viewTransformRef.current.x / 800) * 100}%, ${(viewTransformRef.current.y / 600) * 100}%) scale(${viewTransformRef.current.scale})`;
    }
    const svgContainer = document.getElementById('svg-bg-container');
    if (svgContainer) {
      svgContainer.style.transform = `translate(${(viewTransformRef.current.x / 800) * 100}%, ${(viewTransformRef.current.y / 600) * 100}%) scale(${viewTransformRef.current.scale})`;
      svgContainer.style.setProperty('--svg-stroke-width', `${1 / viewTransformRef.current.scale}px`);
    }
    const zoomEl = document.getElementById('zoom-percentage-display');
    if (zoomEl) zoomEl.innerText = `${Math.round(viewTransformRef.current.scale * 100)}%`;
    
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => {
      drawMap();
    });
  }, [drawMap]);

  // Global Keyboard Shortcuts
  useEffect(() => {
    const handleGlobalKey = (e) => {
      if (e.target.tagName.toLowerCase() === 'input' || e.target.tagName.toLowerCase() === 'textarea') return;
      
      if (e.key === 'Escape' || e.key === 'Backspace' || e.key === 'Delete') {
        if (isCalibratingActive) {
          setScaleCalibration(p => ({ ...p, point1: null, point2: null, distanceMm: '', pixelsPerMm: null }));
          setCalibrationHoverPos(null);
        }
      }
    };
    window.addEventListener('keydown', handleGlobalKey);
    return () => window.removeEventListener('keydown', handleGlobalKey);
  }, [isCalibratingActive, setScaleCalibration]);

  // ─── Canvas events ─────────────────────────────────────────────────
  const getPos = (e) => { 
    const r = canvasRef.current.getBoundingClientRect(); 
    const scaleX = 800 / r.width;
    const scaleY = 600 / r.height;
    const rawX = (e.clientX - r.left) * scaleX;
    const rawY = (e.clientY - r.top) * scaleY;
    return { 
      x: (rawX - viewTransformRef.current.x) / viewTransformRef.current.scale, 
      y: (rawY - viewTransformRef.current.y) / viewTransformRef.current.scale 
    }; 
  };

  const handleCanvasClick = (e) => {
    if (hasDraggedRef.current) {
      hasDraggedRef.current = false;
      return;
    }
    const { x, y } = getPos(e);
    if (isCalibratingActive) {
      if (!scaleCalibration.point1) {
        setScaleCalibration(p => ({ ...p, point1: { x, y }, point2: null }));
      } else if (!scaleCalibration.point2) {
        setScaleCalibration(p => ({ ...p, point2: { x, y } }));
      } else {
        setScaleCalibration(p => ({ ...p, point1: { x, y }, point2: null }));
      }
      return;
    }
    // Note: Area and Corridor selection, multi-selection, and empty-click clearing are fully handled by handleMouseDown & handleMouseUp
    if (activeStep === 6 && selectingForFlow) {
      const clickedArea = mapData.areas.find(a => x >= a.x && x <= a.x + a.w && y >= a.y && y <= a.y + a.h);
      if (clickedArea) {
        if (selectingForFlow.type === 'source' && clickedArea.type === 'dropoff') {
          alert('Source area must be Pickup or Storage.');
          return;
        }
        if (selectingForFlow.type === 'target' && clickedArea.type === 'pickup') {
          alert('Target area must be Drop-off or Storage.');
          return;
        }
        setMapData(p => {
          const flows = p.flows.map(f => {
            if (f.id === selectingForFlow.flowId) {
              const field = selectingForFlow.type === 'source' ? 'sourceAreaIds' : 'targetAreaIds';
              const legacyField = selectingForFlow.type === 'source' ? 'sourceAreaId' : 'targetAreaId';
              
              let existing = f[field] || [];
              if (existing.length === 0 && f[legacyField]) {
                 existing = [f[legacyField]];
              }
              
              let newArr;
              if (existing.includes(clickedArea.id)) {
                 newArr = existing.filter(id => id !== clickedArea.id);
              } else {
                 newArr = [...existing, clickedArea.id];
              }
              
              return { ...f, [field]: newArr, [legacyField]: undefined };
            }
            return f;
          });
          return { ...p, flows };
        });
      }
      return;
    }
    if (mode === 'measure') {
      if (!measuringLine || measuringLine.state === 'done') {
        setMeasuringLine({ state: 'drawing', start: { x, y }, end: { x, y } });
      } else if (measuringLine.state === 'drawing') {
        setMeasuringLine(p => ({ ...p, state: 'done', end: { x, y } }));
      }
      return;
    }
  };

  const handleMouseDown = (e) => {
    // Unfocus any active inputs so keyboard shortcuts route to canvas
    if (document.activeElement && ['INPUT', 'TEXTAREA', 'SELECT'].includes(document.activeElement.tagName)) {
      document.activeElement.blur();
    }
    if (canvasRef.current && document.activeElement !== canvasRef.current) {
      canvasRef.current.focus();
    }

    // Right click ALWAYS pans (or middle click, or pan mode)
    if (e.button === 2 || e.button === 1 || mode === 'pan') {
      panRef.current = { active: true, x: e.clientX, y: e.clientY };
      setIsPanning(true);
      return;
    }
    
    // Left click is used for interaction (select/draw/resize)
    if (e.button !== 0) return;
    if (isCalibratingActive) return;

    const { x, y } = getPos(e);
    dragStartPosRef.current = { x, y };
    hasDraggedRef.current = false;

    // Check handles of ALREADY selected single item (only when in select mode)
    let targetItem = selectedArea || selectedCorridor;
    let clickedHandle = null;
    if (mode === 'select' && targetItem && selectedAreaIds.length <= 1 && selectedCorridorIds.length <= 1) {
      const uiScale = 1 / viewTransformRef.current.scale;
      const maxHandleHit = Math.min(6 * uiScale, Math.min(Math.abs(targetItem.w), Math.abs(targetItem.h)) * 0.22);
      const tx0 = targetItem.w < 0 ? targetItem.x + targetItem.w : targetItem.x;
      const ty0 = targetItem.h < 0 ? targetItem.y + targetItem.h : targetItem.y;
      const tw = Math.abs(targetItem.w);
      const th = Math.abs(targetItem.h);
      const handles = [
        { id: 'nw', x: tx0, y: ty0 },
        { id: 'ne', x: tx0 + tw, y: ty0 },
        { id: 'sw', x: tx0, y: ty0 + th },
        { id: 'se', x: tx0 + tw, y: ty0 + th }
      ];
      clickedHandle = handles.find(h => Math.abs(x - h.x) <= maxHandleHit && Math.abs(y - h.y) <= maxHandleHit);
    }
    
    if (clickedHandle) {
      const isArea = targetItem === selectedArea;
      setDragState({ type: 'resize', itemType: isArea ? 'area' : 'corridor', targetId: targetItem.id, startX: x, startY: y, initialRect: { ...targetItem }, handle: clickedHandle.id });
      return;
    }

    if (activeStep === 5 && mode === 'select') {
      const clickedNode = mapData.nodes.find(n => Math.hypot(n.x - x, n.y - y) <= 10);
      if (clickedNode && clickedNode.type !== 'normal') {
        setSelectedNode(clickedNode);
        setSelectedCorridor(null);
        setSelectedCorridorIds([]);
        setSelectedArea(null);
        setSelectedAreaIds([]);
        return;
      }
    }

    // Check which area or corridor was clicked under cursor (with 5px forgiving padding)
    const clickedArea = mapData.areas.find(a => {
      const ax = Number(a.x) || 0, ay = Number(a.y) || 0, aw = Number(a.w) || 0, ah = Number(a.h) || 0;
      const x0 = Math.min(ax, ax + aw), y0 = Math.min(ay, ay + ah);
      const x1 = Math.max(ax, ax + aw), y1 = Math.max(ay, ay + ah);
      const pad = 5;
      return x >= x0 - pad && x <= x1 + pad && y >= y0 - pad && y <= y1 + pad;
    });

    const clickedCorridor = !clickedArea && (mapData.corridors || []).find(c => {
      const cx = Number(c.x) || 0, cy = Number(c.y) || 0, cw = Number(c.w) || 0, ch = Number(c.h) || 0;
      const x0 = Math.min(cx, cx + cw), y0 = Math.min(cy, cy + ch);
      const x1 = Math.max(cx, cx + cw), y1 = Math.max(cy, cy + ch);
      const pad = 5;
      return x >= x0 - pad && x <= x1 + pad && y >= y0 - pad && y <= y1 + pad;
    });

    // IF an existing area or corridor was clicked (even if mode was 'draw_area' or 'draw_corridor'):
    if (clickedArea || clickedCorridor) {
      setMode('select'); // Automatically switch to select mode!

      const isAlreadySelected = (clickedArea && selectedAreaIds.includes(clickedArea.id)) ||
                                (clickedCorridor && selectedCorridorIds.includes(clickedCorridor.id));

      if (isAlreadySelected) {
        if (e.shiftKey) {
          if (clickedArea) {
            const next = selectedAreaIds.filter(id => id !== clickedArea.id);
            setSelectedAreaIds(next);
            setSelectedArea(next.length === 1 ? (mapData.areas.find(a => a.id === next[0]) || null) : null);
          } else if (clickedCorridor) {
            const next = selectedCorridorIds.filter(id => id !== clickedCorridor.id);
            setSelectedCorridorIds(next);
            setSelectedCorridor(next.length === 1 ? ((mapData.corridors || []).find(c => c.id === next[0]) || null) : null);
          }
          return;
        }

        // Start multi-move or single-move for the group
        const initialAreas = mapData.areas.filter(a => selectedAreaIds.includes(a.id)).map(a => ({ id: a.id, x: Number(a.x) || 0, y: Number(a.y) || 0 }));
        const initialCorridors = (mapData.corridors || []).filter(c => selectedCorridorIds.includes(c.id)).map(c => ({ id: c.id, x: Number(c.x) || 0, y: Number(c.y) || 0 }));
        
        if (initialAreas.length > 1 || initialCorridors.length > 1 || (initialAreas.length + initialCorridors.length > 1)) {
          setDragState({
            type: 'move_multi',
            startX: x,
            startY: y,
            initialAreas,
            initialCorridors,
          });
        } else if (clickedArea) {
          setDragState({ type: 'move', itemType: 'area', targetId: clickedArea.id, startX: x, startY: y, initialRect: { ...clickedArea } });
        } else if (clickedCorridor) {
          setDragState({ type: 'move', itemType: 'corridor', targetId: clickedCorridor.id, startX: x, startY: y, initialRect: { ...clickedCorridor } });
        }
        return;
      }

      // Clicked a new area/corridor not currently selected
      if (clickedArea) {
        if (e.shiftKey) {
          const next = [...selectedAreaIds, clickedArea.id];
          setSelectedAreaIds(next);
          setSelectedArea(next.length === 1 ? clickedArea : null);
        } else {
          setSelectedArea(clickedArea);
          setSelectedAreaIds([clickedArea.id]);
          setSelectedCorridor(null);
          setSelectedCorridorIds([]);
          setDragState({ type: 'move', itemType: 'area', targetId: clickedArea.id, startX: x, startY: y, initialRect: { ...clickedArea } });
        }
        return;
      }

      if (clickedCorridor) {
        if (e.shiftKey) {
          const next = [...selectedCorridorIds, clickedCorridor.id];
          setSelectedCorridorIds(next);
          setSelectedCorridor(next.length === 1 ? clickedCorridor : null);
        } else {
          setSelectedCorridor(clickedCorridor);
          setSelectedCorridorIds([clickedCorridor.id]);
          setSelectedArea(null);
          setSelectedAreaIds([]);
          setDragState({ type: 'move', itemType: 'corridor', targetId: clickedCorridor.id, startX: x, startY: y, initialRect: { ...clickedCorridor } });
        }
        return;
      }
    }

    // If clicked on EMPTY space:
    if (mode === 'select') {
      const box = { startX: x, startY: y, curX: x, curY: y, isShift: e.shiftKey };
      selectionBoxRef.current = box;
      setSelectionBox(box);
      return;
    }

    if (mode === 'draw_area' || mode === 'draw_corridor' || mode === 'zoom_window' || mode === 'erase' || mode === 'crop') {
      setDrawingRect({ startX: x, startY: y, curX: x, curY: y });
    }
  };

  const handleMouseMove = (e) => { 
    if (panRef.current.active) {
      const dx = e.clientX - panRef.current.x;
      const dy = e.clientY - panRef.current.y;
      panRef.current.x = e.clientX;
      panRef.current.y = e.clientY;
      const r = canvasRef.current.getBoundingClientRect();
      const scaleX = canvasRef.current.width / r.width;
      const scaleY = canvasRef.current.height / r.height;
      viewTransformRef.current.x += dx * scaleX;
      viewTransformRef.current.y += dy * scaleY;
      applyViewTransform();
      return;
    }

    if (selectionBoxRef.current) {
      const { x, y } = getPos(e);
      if (Math.hypot(x - dragStartPosRef.current.x, y - dragStartPosRef.current.y) > 3) {
        hasDraggedRef.current = true;
      }
      selectionBoxRef.current.curX = x;
      selectionBoxRef.current.curY = y;
      setSelectionBox({ ...selectionBoxRef.current });
      return;
    }

    if (!dragState && mode === 'select' && canvasRef.current) {
      const { x, y } = getPos(e);
      const isOver = mapData.areas.some(a => {
        const ax0 = a.w < 0 ? a.x + a.w : a.x;
        const ay0 = a.h < 0 ? a.y + a.h : a.y;
        return x >= ax0 && x <= ax0 + Math.abs(a.w) && y >= ay0 && y <= ay0 + Math.abs(a.h);
      }) || (mapData.corridors || []).some(c => {
        const cx0 = c.w < 0 ? c.x + c.w : c.x;
        const cy0 = c.h < 0 ? c.y + c.h : c.y;
        return x >= cx0 && x <= cx0 + Math.abs(c.w) && y >= cy0 && y <= cy0 + Math.abs(c.h);
      });
      canvasRef.current.style.cursor = isOver ? 'move' : 'default';
    }

    if (dragState) {
      const { x, y } = getPos(e);
      const dx = x - dragState.startX;
      const dy = y - dragState.startY;
      if (Math.hypot(dx, dy) > 3) {
        hasDraggedRef.current = true;
      }

      if (dragState.type === 'move_multi') {
        const areaMap = new Map((dragState.initialAreas || []).map(ia => [ia.id, ia]));
        const corMap = new Map((dragState.initialCorridors || []).map(ic => [ic.id, ic]));

        setMapData(p => ({
          ...p,
          areas: p.areas.map(a => {
            const init = areaMap.get(a.id);
            if (!init) return a;
            return { ...a, x: Math.round(init.x + dx), y: Math.round(init.y + dy) };
          }),
          corridors: (p.corridors || []).map(c => {
            const init = corMap.get(c.id);
            if (!init) return c;
            return { ...c, x: Math.round(init.x + dx), y: Math.round(init.y + dy) };
          })
        }));
        return;
      }
      
      setMapData(p => {
        const updateList = (list) => list.map(item => {
          if (item.id !== dragState.targetId) return item;
          let newRect = { ...item };
          if (dragState.type === 'move') {
            newRect.x = Math.round(dragState.initialRect.x + dx);
            newRect.y = Math.round(dragState.initialRect.y + dy);
          } else if (dragState.type === 'resize') {
            const ir = dragState.initialRect;
            if (dragState.handle.includes('w')) {
              newRect.x = Math.min(ir.x + ir.w - 10, ir.x + dx);
              newRect.w = ir.x + ir.w - newRect.x;
            }
            if (dragState.handle.includes('e')) {
              newRect.w = Math.max(10, ir.w + dx);
            }
            if (dragState.handle.includes('n')) {
              newRect.y = Math.min(ir.y + ir.h - 10, ir.y + dy);
              newRect.h = ir.y + ir.h - newRect.y;
            }
            if (dragState.handle.includes('s')) {
              newRect.h = Math.max(10, ir.h + dy);
            }
          }
          
          if (dragState.itemType === 'area') setSelectedArea(newRect);
          else setSelectedCorridor(newRect);
          
          return newRect;
        });
        
        return {
          ...p,
          areas: dragState.itemType === 'area' ? updateList(p.areas) : p.areas,
          corridors: dragState.itemType === 'corridor' ? updateList(p.corridors || []) : (p.corridors || [])
        };
      });
      return;
    }

    if (isCalibratingActive && scaleCalibration.point1 && !scaleCalibration.point2) {
      const { x, y } = getPos(e);
      setCalibrationHoverPos({ x, y });
    }
    if (mode === 'measure' && measuringLine?.state === 'drawing') {
      const { x, y } = getPos(e);
      setMeasuringLine(p => ({ ...p, end: { x, y } }));
    }
    if (drawingRect) { 
      const { x, y } = getPos(e); 
      setDrawingRect(p => ({ ...p, curX: x, curY: y })); 
    } 
  };

  const handleMouseUp = () => {
    if (panRef.current.active) {
      panRef.current.active = false;
      setIsPanning(false);
      return;
    }
    if (dragState) {
      setDragState(null);
      return;
    }

    if (selectionBoxRef.current) {
      const box = selectionBoxRef.current;
      selectionBoxRef.current = null;
      setSelectionBox(null);

      const sx = Math.min(box.startX, box.curX);
      const sy = Math.min(box.startY, box.curY);
      const sw = Math.abs(box.curX - box.startX);
      const sh = Math.abs(box.curY - box.startY);

      if (sw > 4 && sh > 4) {
        hasDraggedRef.current = true;
        const bx0 = sx, by0 = sy, bx1 = sx + sw, by1 = sy + sh;

        // Check bounding box intersection
        const enclosedAreas = mapData.areas.filter(a => {
          const ax = Number(a.x) || 0, ay = Number(a.y) || 0, aw = Number(a.w) || 0, ah = Number(a.h) || 0;
          const ax0 = Math.min(ax, ax + aw), ay0 = Math.min(ay, ay + ah);
          const ax1 = Math.max(ax, ax + aw), ay1 = Math.max(ay, ay + ah);
          return !(ax1 < bx0 || ax0 > bx1 || ay1 < by0 || ay0 > by1);
        });

        const enclosedCorridors = (mapData.corridors || []).filter(c => {
          const cx = Number(c.x) || 0, cy = Number(c.y) || 0, cw = Number(c.w) || 0, ch = Number(c.h) || 0;
          const cx0 = Math.min(cx, cx + cw), cy0 = Math.min(cy, cy + ch);
          const cx1 = Math.max(cx, cx + cw), cy1 = Math.max(cy, cy + ch);
          return !(cx1 < bx0 || cx0 > bx1 || cy1 < by0 || cy0 > by1);
        });

        const newAreaIds = enclosedAreas.map(a => a.id);
        const newCorridorIds = enclosedCorridors.map(c => c.id);

        if (box.isShift) {
          const mergedAreas = Array.from(new Set([...selectedAreaIds, ...newAreaIds]));
          const mergedCorridors = Array.from(new Set([...selectedCorridorIds, ...newCorridorIds]));
          setSelectedAreaIds(mergedAreas);
          setSelectedCorridorIds(mergedCorridors);
          if (mergedAreas.length === 1 && mergedCorridors.length === 0) {
            setSelectedArea(mapData.areas.find(a => a.id === mergedAreas[0]) || null);
          } else {
            setSelectedArea(null);
          }
        } else {
          setSelectedAreaIds(newAreaIds);
          setSelectedCorridorIds(newCorridorIds);
          if (newAreaIds.length === 1 && newCorridorIds.length === 0) {
            setSelectedArea(enclosedAreas[0]);
          } else {
            setSelectedArea(null);
          }
          if (newCorridorIds.length === 1 && newAreaIds.length === 0) {
            setSelectedCorridor(enclosedCorridors[0]);
          } else {
            setSelectedCorridor(null);
          }
        }
      } else if (!box.isShift) {
        setSelectedArea(null);
        setSelectedAreaIds([]);
        setSelectedCorridor(null);
        setSelectedCorridorIds([]);
      }
      return;
    }
    if (!drawingRect) return;
    const ax = Math.min(drawingRect.startX, drawingRect.curX), ay = Math.min(drawingRect.startY, drawingRect.curY);
    const aw = Math.abs(drawingRect.curX - drawingRect.startX), ah = Math.abs(drawingRect.curY - drawingRect.startY);
    
    if (mode === 'erase') {
      if (aw > 5 && ah > 5) {
        const canvasRect = canvasRef.current.getBoundingClientRect();
        const sx = canvasRect.left + (ax * viewTransformRef.current.scale + viewTransformRef.current.x) * (canvasRect.width / 800);
        const sy = canvasRect.top + (ay * viewTransformRef.current.scale + viewTransformRef.current.y) * (canvasRect.height / 600);
        const sw = (aw * viewTransformRef.current.scale) * (canvasRect.width / 800);
        const sh = (ah * viewTransformRef.current.scale) * (canvasRect.height / 600);

        const svgContainer = document.getElementById('svg-bg-container');
        if (svgContainer) {
          const elements = svgContainer.querySelectorAll('path, line, circle, ellipse, polyline, polygon, rect');
          const toHide = [];
          elements.forEach(el => {
            if (el.style.display === 'none') return;
            const r = el.getBoundingClientRect();
            if (r.width === 0 && r.height === 0) return; // Skip invisible elements
            // Check intersection
            if (r.right >= sx && r.left <= sx + sw && r.bottom >= sy && r.top <= sy + sh) {
              toHide.push(el);
            }
          });
          
          if (toHide.length > 0) {
            toHide.forEach(el => el.style.display = 'none');
            
            // Serialize back and clear bgImageData so the rasterizer re-generates with erased lines
            const newSvgContent = new XMLSerializer().serializeToString(svgContainer.querySelector('svg') || svgContainer);
            setMapData(p => ({ ...p, svgContent: newSvgContent || svgContainer.innerHTML, bgImageData: null }));
          }
        }
      }
      setDrawingRect(null);
      return;
    }
    
    if (mode === 'crop') {
      if (aw > 5 && ah > 5) {
        const ob = mapData.bgOriginalViewBox;
        if (ob && mapData.svgContent) {
          // Calculate the new logical view box based on the user's drawing rectangle
          // ax, ay, aw, ah are in the canvas's logical 800x600 space.
          // Because the SVG uses 'xMidYMid meet', it might have letterboxing. We must calculate the actual rendered scale and offset.
          const scaleX = 800 / ob.w;
          const scaleY = 600 / ob.h;
          const scale = Math.min(scaleX, scaleY);
          
          const renderedW = ob.w * scale;
          const renderedH = ob.h * scale;
          const offsetX = (800 - renderedW) / 2;
          const offsetY = (600 - renderedH) / 2;
          
          // Map the canvas coordinates back to the SVG's intrinsic viewBox coordinates
          const newX = ob.x + (ax - offsetX) / scale;
          const newY = ob.y + (ay - offsetY) / scale;
          const newW = aw / scale;
          const newH = ah / scale;
          
          // Re-center viewTransform since the cropped map will fill the canvas again
          viewTransformRef.current = { x: 0, y: 0, scale: 1 };
          applyViewTransform();
          committedViewTransformRef.current = { x: 0, y: 0, scale: 1 };
          
          // Replace the viewBox inside the SVG content string
          const updatedSvgContent = mapData.svgContent.replace(/viewBox="[^"]+"/, `viewBox="${newX} ${newY} ${newW} ${newH}"`);
          
          setMapData(p => {
            const newScaleCanvas = Math.min(800 / newW, 600 / newH);
            const ratio = newScaleCanvas / scale;
            const newOffsetX = (800 - newW * newScaleCanvas) / 2;
            const newOffsetY = (600 - newH * newScaleCanvas) / 2;

            const mapX = cx => (ob.x + (cx - offsetX) / scale - newX) * newScaleCanvas + newOffsetX;
            const mapY = cy => (ob.y + (cy - offsetY) / scale - newY) * newScaleCanvas + newOffsetY;
            const mapS = val => val * ratio;

            return {
              ...p,
              bgOriginalViewBox: { x: newX, y: newY, w: newW, h: newH },
              svgContent: updatedSvgContent,
              bgImageData: null,
              scaleCalibration: p.scaleCalibration?.pixelsPerMm ? {
                ...p.scaleCalibration,
                pixelsPerMm: p.scaleCalibration.pixelsPerMm * ratio
              } : p.scaleCalibration,
              areas: (p.areas || []).map(a => ({ ...a, x: mapX(a.x), y: mapY(a.y), w: mapS(a.w), h: mapS(a.h) })),
              corridors: (p.corridors || []).map(c => ({ ...c, x: mapX(c.x), y: mapY(c.y), w: mapS(c.w), h: mapS(c.h) })),
              nodes: (p.nodes || []).map(n => ({ ...n, x: mapX(n.x), y: mapY(n.y) }))
            };
          });
        }
      }
      setMode('select');
      setDrawingRect(null);
      return;
    }

    if (mode === 'zoom_window') {
      if (aw > 0 && ah > 0) {
        const canvasW = 800;
        const canvasH = 600;
        const newScale = Math.min(5000, Math.min(canvasW / aw, canvasH / ah));
        const cx = ax + aw / 2;
        const cy = ay + ah / 2;
        viewTransformRef.current = {
          scale: newScale,
          x: (canvasW / 2) - (cx * newScale),
          y: (canvasH / 2) - (cy * newScale)
        };
        applyViewTransform();
      }
      setDrawingRect(null);
      setMode('select');
      return;
    }

    if (aw > 2 && ah > 2) {
      if (mode === 'draw_area') {
        const defaultRackConfig = {
          levels: 4,
          aisleWidth: activeRackType === 'storage_selective_rack' ? 3000 : 1600,
          backToBack: 150,
          margin: 100,
          beamThickness: 100,
          airGap: 300,
          palletsPerBay: 2,
          orientation: 'auto',
          singleRowStart: false,
          hasBottomBeam: true,
        };
        const first3D = available3DModelsRef.current?.[0];
        let finalW = aw;
        let finalH = ah;
        let finalX = ax;
        let finalY = ay;

        if (activeRackType === 'model_3d') {
          const ppm = scaleCalibration.pixelsPerMm || 0.0125;
          const detectedType = detectModelType(first3D);

          let reqW = 1400 * ppm;
          let reqH = 2800 * ppm;
          if (detectedType === 'power_pallet') {
            reqW = 800 * ppm;
            reqH = 2200 * ppm;
          } else if (detectedType === 'pallet_station') {
            reqW = 1300 * ppm;
            reqH = 1400 * ppm;
          } else if (detectedType === 'inbound_station') {
            reqW = 2400 * ppm;
            reqH = 800 * ppm;
          } else if (detectedType === 'reach_truck') {
            reqW = 1400 * ppm;
            reqH = 2800 * ppm;
          } else if (detectedType === 'forklift') {
            reqW = 1300 * ppm;
            reqH = 3100 * ppm;
          } else if (detectedType === 'operator') {
            reqW = 1000 * ppm;
            reqH = 1000 * ppm;
          } else {
            reqW = 1500 * ppm;
            reqH = 1500 * ppm;
          }

          // If clicked or dragged a small box (< 2.8m in width or < 3.2m in height),
          // snap to the exact physical footprint of the model!
          if (aw < 2800 * ppm || ah < 3200 * ppm) {
            const centerX = ax + aw / 2;
            const centerY = ay + ah / 2;
            finalW = reqW;
            finalH = reqH;
            finalX = centerX - finalW / 2;
            finalY = centerY - finalH / 2;
          }
        }

        const na = { 
          id: `area_${Date.now()}`, 
          x: finalX, y: finalY, w: finalW, h: finalH, 
          type: activeRackType || 'storage_selective_rack', 
          label: activeRackType === 'pickup' ? 'Pickup' :
                 activeRackType === 'dropoff' ? 'Drop-off' :
                 activeRackType === 'conveyor' ? 'Conveyor Line' :
                 activeRackType === 'storage_selective_rack' ? 'Selective Rack' :
                 activeRackType === 'storage_2way_shuttle' ? '2-Way Shuttle Rack' :
                 activeRackType === 'storage_4way_shuttle' ? '4-Way Shuttle Rack' : 
                 activeRackType === 'model_3d' ? (first3D?.name || '3D Asset') : 'Storage Area',
          rackConfig: defaultRackConfig,
          modelConfig: activeRackType === 'model_3d' ? {
            modelId: first3D ? String(first3D.id) : '',
            modelName: first3D?.name || '3D Asset',
            category: first3D?.category || 'reach_truck',
            targetHeight: first3D?.target_height_m ? Number(first3D.target_height_m) : 1.70,
            fileUrl: first3D?.file_url || null,
            rotationDeg: 0
          } : undefined
        };
        setMapData(p => ({ ...p, areas: [...p.areas, na] }));
        setSelectedArea(na);
        setMode('select');
      } else if (mode === 'draw_corridor') {
        const nc = { 
          id: `cor_${Date.now()}`, 
          x: ax, y: ay, w: aw, h: ah, 
          label: 'Corridor',
          lanes: activeLaneType === '1way' ? 1 : 2,
          direction: activeLaneType === 'split' ? 'split' : 'bidirectional'
        };
        setMapData(p => ({ ...p, corridors: [...(p.corridors || []), nc] }));
        setSelectedCorridor(nc);
      }
    }
    setDrawingRect(null);
  };

  // ─── Helpers ───────────────────────────────────────────────────────
  const confirmCalibration = () => {
    const { point1, point2, distanceMm } = scaleCalibration;
    const d = parseFloat(distanceMm);
    if (!point1 || !point2) {
      alert('กรุณาคลิกเลือกจุดบนผังให้ครบ 2 จุด (จุด A และ จุด B)');
      return;
    }
    if (isNaN(d) || d <= 0) {
      alert('กรุณาระบุระยะทางจริงหน่วยมิลลิเมตร (mm) เช่น 10000');
      return;
    }
    const distPx = Math.hypot(point2.x - point1.x, point2.y - point1.y);
    const newPpm = distPx / d;
    setScaleCalibration(p => ({
      ...p,
      point1,
      point2,
      distanceMm: String(d),
      pixelsPerMm: newPpm
    }));
    setIsCalibrating(false);
    setCalibrationHoverPos(null);
    setActiveStep(1);
    setMode('select');
  };

  const cancelCalibration = () => {
    setIsCalibrating(false);
    setCalibrationHoverPos(null);
    setScaleCalibration(p => ({
      ...p,
      point1: null,
      point2: null,
      distanceMm: '',
      pixelsPerMm: p.pixelsPerMm || 0.0125
    }));
    setActiveStep(1);
    setMode('select');
  };

  const copySelected = useCallback(() => {
    let areasToCopy = [];
    if (selectedAreaIds.length > 0) {
      areasToCopy = mapData.areas.filter(a => selectedAreaIds.includes(a.id));
    } else if (selectedArea) {
      areasToCopy = [selectedArea];
    }

    let corridorsToCopy = [];
    if (selectedCorridorIds.length > 0) {
      corridorsToCopy = (mapData.corridors || []).filter(c => selectedCorridorIds.includes(c.id));
    } else if (selectedCorridor) {
      corridorsToCopy = [selectedCorridor];
    }

    if (areasToCopy.length === 0 && corridorsToCopy.length === 0) {
      showToast('กรุณาคลิกเลือกพื้นที่หรือคลุมดำก่อนคัดลอก', 'warning');
      return false;
    }

    clipboardRef.current = {
      areas: JSON.parse(JSON.stringify(areasToCopy)),
      corridors: JSON.parse(JSON.stringify(corridorsToCopy))
    };
    pasteCountRef.current = 1;
    setHasClipboard(true);
    const count = areasToCopy.length + corridorsToCopy.length;
    showToast(`คัดลอก ${count} รายการแล้ว (กด Ctrl+V เพื่อวาง)`, 'success');
    return true;
  }, [selectedAreaIds, selectedArea, selectedCorridorIds, selectedCorridor, mapData.areas, mapData.corridors, showToast]);

  const pasteSelected = useCallback(() => {
    if (!clipboardRef.current) {
      showToast('ยังไม่มีรายการที่คัดลอกไว้ (กดเลือกพื้นที่แล้วกด Ctrl+C ก่อน)', 'warning');
      return false;
    }
    const { areas = [], corridors = [] } = clipboardRef.current;
    if (areas.length === 0 && corridors.length === 0) {
      showToast('ยังไม่มีรายการที่คัดลอกไว้ (กดเลือกพื้นที่แล้วกด Ctrl+C ก่อน)', 'warning');
      return false;
    }

    const offset = pasteCountRef.current * 30;
    pasteCountRef.current += 1;

    const now = Date.now();
    const newAreas = areas.map((a, idx) => {
      const rand = Math.random().toString(36).substring(2, 6);
      return {
        ...JSON.parse(JSON.stringify(a)),
        id: `area_${now}_${idx}_${rand}`,
        x: Math.round(Number(a.x) || 0) + offset,
        y: Math.round(Number(a.y) || 0) + offset,
        w: Number(a.w) || 0,
        h: Number(a.h) || 0
      };
    });

    const newCorridors = corridors.map((c, idx) => {
      const rand = Math.random().toString(36).substring(2, 6);
      return {
        ...JSON.parse(JSON.stringify(c)),
        id: `cor_${now}_${idx}_${rand}`,
        x: Math.round(Number(c.x) || 0) + offset,
        y: Math.round(Number(c.y) || 0) + offset,
        w: Number(c.w) || 0,
        h: Number(c.h) || 0
      };
    });

    setMapData(prev => ({
      ...prev,
      areas: [...prev.areas, ...newAreas],
      corridors: [...(prev.corridors || []), ...newCorridors]
    }));

    setMode('select');

    if (newAreas.length === 1 && newCorridors.length === 0) {
      setSelectedArea(newAreas[0]);
      setSelectedAreaIds([newAreas[0].id]);
      setSelectedCorridor(null);
      setSelectedCorridorIds([]);
    } else if (newCorridors.length === 1 && newAreas.length === 0) {
      setSelectedCorridor(newCorridors[0]);
      setSelectedCorridorIds([newCorridors[0].id]);
      setSelectedArea(null);
      setSelectedAreaIds([]);
    } else {
      setSelectedArea(null);
      setSelectedAreaIds(newAreas.map(a => a.id));
      setSelectedCorridor(null);
      setSelectedCorridorIds(newCorridors.map(c => c.id));
    }

    const count = newAreas.length + newCorridors.length;
    showToast(`วาง ${count} รายการเรียบร้อยแล้ว`, 'success');
    return true;
  }, [showToast, setMapData, setMode]);

  const duplicateSelected = useCallback(() => {
    let areasToCopy = [];
    if (selectedAreaIds.length > 0) {
      areasToCopy = mapData.areas.filter(a => selectedAreaIds.includes(a.id));
    } else if (selectedArea) {
      areasToCopy = [selectedArea];
    }

    let corridorsToCopy = [];
    if (selectedCorridorIds.length > 0) {
      corridorsToCopy = (mapData.corridors || []).filter(c => selectedCorridorIds.includes(c.id));
    } else if (selectedCorridor) {
      corridorsToCopy = [selectedCorridor];
    }

    if (areasToCopy.length === 0 && corridorsToCopy.length === 0) {
      showToast('กรุณาคลิกเลือกพื้นที่ก่อนทำซ้ำ', 'warning');
      return;
    }

    clipboardRef.current = {
      areas: JSON.parse(JSON.stringify(areasToCopy)),
      corridors: JSON.parse(JSON.stringify(corridorsToCopy))
    };
    pasteCountRef.current = 1;
    setHasClipboard(true);

    const now = Date.now();
    const offset = 30;
    const newAreas = areasToCopy.map((a, idx) => {
      const rand = Math.random().toString(36).substring(2, 6);
      return {
        ...JSON.parse(JSON.stringify(a)),
        id: `area_${now}_${idx}_${rand}`,
        x: Math.round(Number(a.x) || 0) + offset,
        y: Math.round(Number(a.y) || 0) + offset,
        w: Number(a.w) || 0,
        h: Number(a.h) || 0
      };
    });

    const newCorridors = corridorsToCopy.map((c, idx) => {
      const rand = Math.random().toString(36).substring(2, 6);
      return {
        ...JSON.parse(JSON.stringify(c)),
        id: `cor_${now}_${idx}_${rand}`,
        x: Math.round(Number(c.x) || 0) + offset,
        y: Math.round(Number(c.y) || 0) + offset,
        w: Number(c.w) || 0,
        h: Number(c.h) || 0
      };
    });

    setMapData(prev => ({
      ...prev,
      areas: [...prev.areas, ...newAreas],
      corridors: [...(prev.corridors || []), ...newCorridors]
    }));

    setMode('select');

    if (newAreas.length === 1 && newCorridors.length === 0) {
      setSelectedArea(newAreas[0]);
      setSelectedAreaIds([newAreas[0].id]);
      setSelectedCorridor(null);
      setSelectedCorridorIds([]);
    } else if (newCorridors.length === 1 && newAreas.length === 0) {
      setSelectedCorridor(newCorridors[0]);
      setSelectedCorridorIds([newCorridors[0].id]);
      setSelectedArea(null);
      setSelectedAreaIds([]);
    } else {
      setSelectedArea(null);
      setSelectedAreaIds(newAreas.map(a => a.id));
      setSelectedCorridor(null);
      setSelectedCorridorIds(newCorridors.map(c => c.id));
    }

    const count = newAreas.length + newCorridors.length;
    showToast(`ทำซ้ำ ${count} รายการเรียบร้อยแล้ว`, 'success');
  }, [selectedAreaIds, selectedArea, selectedCorridorIds, selectedCorridor, mapData.areas, mapData.corridors, showToast, setMapData, setMode]);

  const deleteSelected = useCallback(() => {
    const hasAreaIds = selectedAreaIds.length > 0;
    const hasArea = !!selectedArea;
    const hasCorridorIds = selectedCorridorIds.length > 0;
    const hasCorridor = !!selectedCorridor;

    if (!hasAreaIds && !hasArea && !hasCorridorIds && !hasCorridor) return;

    const areaIdsToDelete = new Set(hasAreaIds ? selectedAreaIds : (selectedArea ? [selectedArea.id] : []));
    const corridorIdsToDelete = new Set(hasCorridorIds ? selectedCorridorIds : (selectedCorridor ? [selectedCorridor.id] : []));
    const count = areaIdsToDelete.size + corridorIdsToDelete.size;

    setMapData(prev => {
      const newAreas = prev.areas.filter(a => !areaIdsToDelete.has(a.id));
      const newCorridors = (prev.corridors || []).filter(c => !corridorIdsToDelete.has(c.id));
      const newFlows = (prev.flows || []).map(f => ({
        ...f,
        sourceAreaIds: (f.sourceAreaIds || (f.sourceAreaId ? [f.sourceAreaId] : [])).filter(id => !areaIdsToDelete.has(id)),
        targetAreaIds: (f.targetAreaIds || (f.targetAreaId ? [f.targetAreaId] : [])).filter(id => !areaIdsToDelete.has(id))
      }));
      return {
        ...prev,
        areas: newAreas,
        corridors: newCorridors,
        flows: newFlows
      };
    });

    setSelectedArea(null);
    setSelectedAreaIds([]);
    setSelectedCorridor(null);
    setSelectedCorridorIds([]);
    showToast(`ลบ ${count} รายการเรียบร้อยแล้ว`, 'info');
  }, [selectedAreaIds, selectedArea, selectedCorridorIds, selectedCorridor, showToast]);

  const updateAreaProp = (k, v) => {
    if (!selectedArea) return;
    const updates = typeof k === 'object' ? k : { [k]: v };
    setMapData(p => ({
      ...p,
      areas: p.areas.map(a => a.id === selectedArea.id ? { ...a, ...updates } : a)
    }));
    setSelectedArea(p => ({ ...p, ...updates }));
  };
  const deleteArea = () => { 
    if (selectedAreaIds.length > 1) {
      deleteSelected();
      return;
    }
    if (!selectedArea && selectedAreaIds.length === 0) return; 
    const targetId = selectedArea?.id || selectedAreaIds[0];
    setMapData(p => {
      const newAreas = p.areas.filter(a => a.id !== targetId);
      const newFlows = (p.flows || []).map(f => ({
        ...f,
        sourceAreaIds: (f.sourceAreaIds || (f.sourceAreaId ? [f.sourceAreaId] : [])).filter(id => id !== targetId),
        targetAreaIds: (f.targetAreaIds || (f.targetAreaId ? [f.targetAreaId] : [])).filter(id => id !== targetId)
      }));
      return { ...p, areas: newAreas, flows: newFlows };
    }); 
    setSelectedArea(null); 
    setSelectedAreaIds([]);
  };

  const updateCorridorProp = (k, v) => { if (!selectedCorridor) return; setMapData(p => ({ ...p, corridors: (p.corridors || []).map(c => c.id === selectedCorridor.id ? { ...c, [k]: v } : c) })); setSelectedCorridor(p => ({ ...p, [k]: v })); };
  const deleteCorridor = () => {
    if (selectedCorridorIds.length > 1) {
      deleteSelected();
      return;
    }
    if (!selectedCorridor && selectedCorridorIds.length === 0) return;
    const targetId = selectedCorridor?.id || selectedCorridorIds[0];
    setMapData(p => ({ ...p, corridors: (p.corridors || []).filter(c => c.id !== targetId) }));
    setSelectedCorridor(null);
    setSelectedCorridorIds([]);
  };

  const deleteConnection = (connKey) => {
    setMapData(prev => ({
      ...prev,
      deletedConns: [...(prev.deletedConns || []), connKey]
    }));
  };

  const cursorClass = (() => {
    if (isCalibratingActive) return 'cursor-crosshair';
    if (mode === 'draw_area' || mode === 'draw_corridor' || mode === 'measure' || mode === 'crop') return 'cursor-crosshair';
    if (activeStep === 6 && selectingForFlow) return 'cursor-crosshair';
    return 'cursor-default';
  })();

  const selectedCount = (selectedAreaIds.length > 0 ? selectedAreaIds.length : (selectedArea ? 1 : 0)) +
                        (selectedCorridorIds.length > 0 ? selectedCorridorIds.length : (selectedCorridor ? 1 : 0));
  const hasSelectedItems = selectedCount > 0;

  useEffect(() => {
    const handleKeyDown = (e) => {
      const activeTag = document.activeElement?.tagName;
      const isInputFocused = activeTag === 'INPUT' || activeTag === 'TEXTAREA' || activeTag === 'SELECT';

      if (e.key === 'Escape') {
        if (isInputFocused && document.activeElement?.blur) {
          document.activeElement.blur();
        }
        setSelectedArea(null);
        setSelectedAreaIds([]);
        setSelectedCorridor(null);
        setSelectedCorridorIds([]);
        setSelectionBox(null);
        setMode('select');
        return;
      }

      if (isInputFocused) return;

      const isCmdOrCtrl = e.ctrlKey || e.metaKey;

      // Copy: 'c', 'C', 'แ' (Thai keyboard), keyCode 67, or code 'KeyC'
      const isKeyC = e.code === 'KeyC' || e.key === 'c' || e.key === 'C' || e.key === 'แ' || e.keyCode === 67;
      if (isCmdOrCtrl && isKeyC) {
        e.preventDefault();
        e.stopPropagation();
        copySelected();
        return;
      }

      // Paste: 'v', 'V', 'อ' (Thai keyboard), keyCode 86, or code 'KeyV'
      const isKeyV = e.code === 'KeyV' || e.key === 'v' || e.key === 'V' || e.key === 'อ' || e.keyCode === 86;
      if (isCmdOrCtrl && isKeyV) {
        e.preventDefault();
        e.stopPropagation();
        pasteSelected();
        return;
      }

      // Duplicate: 'd', 'D', 'ก' (Thai keyboard), keyCode 68, or code 'KeyD'
      const isKeyD = e.code === 'KeyD' || e.key === 'd' || e.key === 'D' || e.key === 'ก' || e.keyCode === 68;
      if (isCmdOrCtrl && isKeyD) {
        e.preventDefault();
        e.stopPropagation();
        duplicateSelected();
        return;
      }

      // Select All: 'a', 'A', 'ฟ' (Thai keyboard), keyCode 65, or code 'KeyA'
      const isKeyA = e.code === 'KeyA' || e.key === 'a' || e.key === 'A' || e.key === 'ฟ' || e.keyCode === 65;
      if (isCmdOrCtrl && isKeyA && (mapData.areas?.length > 0 || mapData.corridors?.length > 0)) {
        e.preventDefault();
        e.stopPropagation();
        setSelectedArea(null);
        setSelectedAreaIds((mapData.areas || []).map(a => a.id));
        setSelectedCorridor(null);
        setSelectedCorridorIds((mapData.corridors || []).map(c => c.id));
        setMode('select');
        showToast(`เลือกทั้งหมด ${(mapData.areas?.length || 0) + (mapData.corridors?.length || 0)} รายการ`, 'info');
        return;
      }

      // Delete: Delete or Backspace
      if (e.key === 'Delete' || e.key === 'Backspace' || e.keyCode === 46 || e.keyCode === 8) {
        const hasSelection = selectedArea || selectedAreaIds.length > 0 || selectedCorridor || selectedCorridorIds.length > 0;
        if (hasSelection) {
          e.preventDefault();
          e.stopPropagation();
          deleteSelected();
          return;
        }
      }
    };

    const handleNativeCopy = (e) => {
      const activeTag = document.activeElement?.tagName;
      if (activeTag === 'INPUT' || activeTag === 'TEXTAREA' || activeTag === 'SELECT') return;
      const hasSelection = selectedArea || selectedAreaIds.length > 0 || selectedCorridor || selectedCorridorIds.length > 0;
      if (hasSelection) {
        e.preventDefault();
        copySelected();
      }
    };

    const handleNativePaste = (e) => {
      const activeTag = document.activeElement?.tagName;
      if (activeTag === 'INPUT' || activeTag === 'TEXTAREA' || activeTag === 'SELECT') return;
      if (clipboardRef.current?.areas?.length > 0 || clipboardRef.current?.corridors?.length > 0) {
        e.preventDefault();
        pasteSelected();
      }
    };

    window.addEventListener('keydown', handleKeyDown, true);
    window.addEventListener('copy', handleNativeCopy, true);
    window.addEventListener('paste', handleNativePaste, true);

    return () => {
      window.removeEventListener('keydown', handleKeyDown, true);
      window.removeEventListener('copy', handleNativeCopy, true);
      window.removeEventListener('paste', handleNativePaste, true);
    };
  }, [copySelected, pasteSelected, duplicateSelected, deleteSelected, selectedArea, selectedAreaIds, selectedCorridor, selectedCorridorIds, mapData.areas, mapData.corridors, showToast]);

  // ─── Render ────────────────────────────────────────────────────────
  const canvasWrapperRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = React.useState(false);

  const toggleFullscreen = () => {
    const el = canvasWrapperRef.current;
    if (!el) return;
    if (!document.fullscreenElement) {
      el.requestFullscreen().then(() => setIsFullscreen(true)).catch(() => {});
    } else {
      document.exitFullscreen().then(() => setIsFullscreen(false)).catch(() => {});
    }
  };

  useEffect(() => {
    const onFsChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onFsChange);
    return () => document.removeEventListener('fullscreenchange', onFsChange);
  }, []);

  return (
    <div className="flex h-full flex-col">

      {/* Body */}
      <div className="flex flex-1 overflow-hidden">
        {/* Canvas Area */}
        <div className="flex-1 bg-gray-50 overflow-auto flex items-center justify-center p-4">
          <div ref={canvasWrapperRef} className="relative shadow border flex-1 h-full overflow-hidden bg-gray-100 flex items-center justify-center">
            {/* Fullscreen Toggle Button */}
            <button
              onClick={toggleFullscreen}
              title={isFullscreen ? 'Exit Fullscreen' : 'Expand to Fullscreen'}
              className="absolute top-3 right-3 z-20 bg-white border border-gray-200 shadow-md rounded-lg p-2 hover:bg-gray-50 transition-colors text-gray-600 hover:text-gray-900"
            >
              {isFullscreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
            </button>

            {/* View/Edit/3D Mode & 3D Controls Toolbar */}
            <div className="absolute top-3 left-3 z-20 flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <div className="flex bg-white border border-gray-200 shadow-md rounded-lg overflow-hidden">
                  <button 
                    onClick={() => { setDisplayMode('view'); if(mode === 'erase') setMode('pan'); }}
                    className={`px-3 py-1.5 text-sm font-medium flex items-center gap-1.5 ${displayMode === 'view' ? 'bg-blue-50 text-blue-700' : 'hover:bg-gray-50 text-gray-600'}`}
                  >
                    <Eye size={14} /> View Mode
                  </button>
                  <button 
                    onClick={() => { setDisplayMode('edit'); setMode('erase'); }}
                    className={`px-3 py-1.5 text-sm font-medium flex items-center gap-1.5 border-l ${displayMode === 'edit' ? 'bg-orange-50 text-orange-700' : 'hover:bg-gray-50 text-gray-600'}`}
                  >
                    <Edit2 size={14} /> Edit Mode
                  </button>
                  <button 
                    onClick={() => setDisplayMode('3d')}
                    className={`px-3 py-1.5 text-sm font-medium flex items-center gap-1.5 border-l ${displayMode === '3d' ? 'bg-indigo-50 text-indigo-700' : 'hover:bg-gray-50 text-gray-600'}`}
                  >
                    <Box size={14} /> 3D View
                  </button>
                </div>

                {/* 3D Action Controls on the first row */}
                {displayMode === '3d' && (
                  <div className="flex items-center bg-white border border-gray-200 shadow-md rounded-lg overflow-hidden divide-x divide-gray-100 animate-fadeIn">
                    {/* Lock/Unlock Toggle */}
                    <button
                      type="button"
                      onClick={() => {
                        const nextLocked = !is3DPositionLocked;
                        setIs3DPositionLocked(nextLocked);
                        showToast(
                          nextLocked ? '🔒 ล็อกตำแหน่งพื้นที่แล้ว (ห้ามเลื่อน)' : '🔓 ปลดล็อกแล้ว: สามารถคลิกซ้ายลากย้ายตำแหน่งได้',
                          nextLocked ? 'info' : 'success'
                        );
                      }}
                      className={`px-3 py-1.5 text-xs font-semibold flex items-center gap-1.5 transition-colors cursor-pointer select-none ${
                        is3DPositionLocked
                          ? 'bg-amber-50 text-amber-900 hover:bg-amber-100'
                          : 'bg-emerald-50 text-emerald-800 hover:bg-emerald-100'
                      }`}
                      title={is3DPositionLocked ? "คลิกเพื่อปลดล็อกให้สามารถลากย้ายตำแหน่งได้" : "คลิกเพื่อล็อกตำแหน่งพื้นที่"}
                    >
                      <span>{is3DPositionLocked ? '🔒' : '🔓'}</span>
                      <span>{is3DPositionLocked ? 'ล็อกตำแหน่ง' : 'ปลดล็อกแล้ว'}</span>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${
                        is3DPositionLocked ? 'bg-amber-200/80 text-amber-900' : 'bg-emerald-200/80 text-emerald-950 animate-pulse'
                      }`}>
                        {is3DPositionLocked ? 'ห้ามเลื่อน' : 'ย้ายได้'}
                      </span>
                    </button>

                    {/* Show Dimensions Toggle */}
                    <label className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 cursor-pointer select-none">
                      <input 
                        type="checkbox" 
                        checked={show3DDimensions} 
                        onChange={() => setShow3DDimensions(!show3DDimensions)} 
                        className="w-3.5 h-3.5 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                      />
                      <span>Show Dimensions</span>
                    </label>
                  </div>
                )}
              </div>

              {/* Row 2: 3D Camera View Presets (Isometric, Top, Side, Front) - Placed directly underneath */}
              {displayMode === '3d' && (
                <div className="flex items-center self-start bg-white border border-gray-200 shadow-md rounded-lg overflow-hidden divide-x divide-gray-100 animate-fadeIn text-xs">
                  <button
                    type="button"
                    onClick={() => {
                      setCameraPreset({ type: 'isometric', ts: Date.now() });
                      showToast('ปรับมุมมอง: ไอโซเมตริก (Isometric 3D)', 'info');
                    }}
                    className={`px-3 py-1.5 font-medium transition-colors cursor-pointer select-none ${
                      cameraPreset?.type === 'isometric'
                        ? 'bg-indigo-600 text-white font-semibold'
                        : 'hover:bg-gray-50 text-gray-700'
                    }`}
                    title="มุมมองไอโซเมตริก 3 มิติ (Isometric View - 45°)"
                  >
                    Isometric
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      setCameraPreset({ type: 'top', ts: Date.now() });
                      showToast('ปรับมุมมอง: ด้านบน (Top View)', 'info');
                    }}
                    className={`px-3 py-1.5 font-medium transition-colors cursor-pointer select-none ${
                      cameraPreset?.type === 'top'
                        ? 'bg-indigo-600 text-white font-semibold'
                        : 'hover:bg-gray-50 text-gray-700'
                    }`}
                    title="มุมมองด้านบน แปลนพื้น 2D/3D (Top View - Bird's eye)"
                  >
                    Top View
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      setCameraPreset({ type: 'side', ts: Date.now() });
                      showToast('ปรับมุมมอง: ด้านข้าง (Side View)', 'info');
                    }}
                    className={`px-3 py-1.5 font-medium transition-colors cursor-pointer select-none ${
                      cameraPreset?.type === 'side'
                        ? 'bg-indigo-600 text-white font-semibold'
                        : 'hover:bg-gray-50 text-gray-700'
                    }`}
                    title="มุมมองด้านข้าง ระดับสายตา (Side View - Elevation)"
                  >
                    Side View
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      setCameraPreset({ type: 'front', ts: Date.now() });
                      showToast('ปรับมุมมอง: ด้านหน้า (Front View)', 'info');
                    }}
                    className={`px-3 py-1.5 font-medium transition-colors cursor-pointer select-none ${
                      cameraPreset?.type === 'front'
                        ? 'bg-indigo-600 text-white font-semibold'
                        : 'hover:bg-gray-50 text-gray-700'
                    }`}
                    title="มุมมองด้านหน้า (Front View)"
                  >
                    Front View
                  </button>
                </div>
              )}
            </div>
            
            <div className="relative w-full h-auto bg-white" style={{ maxWidth: '100%', maxHeight: '100%', aspectRatio: '800/600' }}>
              
              {/* Background Vector/Image Layer */}
              {displayMode !== '3d' && (svgBlobUrl || mapData.bgImageData) && (
                <div style={{
                  position: 'absolute', left: 0, top: 0, width: '100%', height: '100%',
                  overflow: 'hidden',
                  pointerEvents: 'none'
                }}>
                  <div 
                    ref={bgImageRef}
                    style={{
                      position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
                      transformOrigin: '0 0',
                      transform: `translate(${(viewTransformRef.current.x / 800) * 100}%, ${(viewTransformRef.current.y / 600) * 100}%) scale(${viewTransformRef.current.scale})`,
                    }}
                  >
                    <img 
                      src={svgBlobUrl || mapData.bgImageData}
                      style={{
                        width: '100%', height: '100%',
                        objectFit: 'contain',
                        transformOrigin: '50% 50%',
                        transform: `rotate(${mapData.bgSettings?.rotation ?? 0}deg)`,
                        opacity: (displayMode === 'edit' && mapData.svgContent) ? 0 : (mapData.bgSettings?.opacity ?? 1),
                        filter: (mapData.bgSettings?.grayscale ?? 0) > 0 ? `grayscale(${mapData.bgSettings.grayscale}%)` : 'none',
                      }}
                      alt="layout"
                    />
                  </div>
                </div>
              )}

              {/* Invisible SVG container for editing lines */}
              {mapData.svgContent && displayMode === 'edit' && (
                <>
                  <style>{`
                    #svg-bg-container {
                      --svg-stroke-width: ${1 / viewTransformRef.current.scale}px;
                    }
                    #svg-bg-container path,
                    #svg-bg-container line,
                    #svg-bg-container polyline,
                    #svg-bg-container polygon,
                    #svg-bg-container circle,
                    #svg-bg-container ellipse {
                      vector-effect: non-scaling-stroke !important;
                      stroke-width: var(--svg-stroke-width) !important;
                    }
                  `}</style>
                  <div 
                  id="svg-bg-container"
                  style={{
                    position: 'absolute',
                    top: 0, left: 0,
                    width: '100%', height: '100%',
                    transformOrigin: '0 0',
                    transform: `translate(${(viewTransformRef.current.x / 800) * 100}%, ${(viewTransformRef.current.y / 600) * 100}%) scale(${viewTransformRef.current.scale})`,
                    pointerEvents: 'none',
                    opacity: mapData.bgSettings?.opacity ?? 1,
                  }}
                  dangerouslySetInnerHTML={{ 
                    __html: mapData.svgContent 
                      ? mapData.svgContent
                          .replace(/width="[^"]+"/, 'width="100%"')
                          .replace(/height="[^"]+"/, 'height="100%"') 
                      : '' 
                  }}
                />
                </>
              )}
              {/* Center Startup Choice Overlay */}
              {!isStarted && !mapData.bgImageData && !mapData.svgContent && (!mapData.areas || mapData.areas.length === 0) && (
                <div className="absolute inset-0 flex flex-col items-center justify-center z-20 pointer-events-none p-4">
                  <div className="bg-white p-7 sm:p-8 rounded-2xl shadow-2xl border border-gray-100 pointer-events-auto text-center flex flex-col items-center gap-4 max-w-sm w-full mx-4" style={{ boxShadow: '0 20px 50px -10px rgba(0,0,0,0.15)' }}>
                    <div className="w-16 h-16 bg-blue-50 text-blue-500 rounded-full flex items-center justify-center mb-1">
                      <Upload size={32} strokeWidth={1.5} />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-800">Upload Layout</h3>
                      <p className="text-sm text-gray-500 mt-2 leading-relaxed">
                        Upload layout file เป็น JPG/PNG/PDF/DWG เพื่อเริ่มต้นจำลองพื้นที่
                      </p>
                    </div>

                    {/* Button 1: Browse Files */}
                    <label className={`mt-2 w-full flex justify-center items-center gap-2 ${isUploading ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700 cursor-pointer'} text-white px-5 py-3 rounded-xl font-medium shadow-sm transition-all`}>
                      {isUploading ? (
                        <><div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div> Loading...</>
                      ) : (
                        <>Browse Files</>
                      )}
                      <input type="file" accept="image/*,application/pdf,.dwg" className="hidden" onChange={(e) => { setIsStarted(true); handleImageUpload(e); }} disabled={isUploading} />
                    </label>

                    {/* Divider */}
                    <div className="flex items-center gap-3 w-full my-0.5">
                      <div className="flex-1 h-px bg-gray-200"></div>
                      <span className="text-xs text-gray-400 font-medium">หรือ</span>
                      <div className="flex-1 h-px bg-gray-200"></div>
                    </div>

                    {/* Button 2: Start with Blank Floor */}
                    <button
                      type="button"
                      onClick={handleStartBlank}
                      className="w-full flex justify-center items-center gap-2 bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 hover:border-gray-400 px-5 py-3 rounded-xl font-semibold shadow-xs transition-all cursor-pointer text-sm"
                    >
                      <LayoutGrid size={18} className="text-blue-600" />
                      เริ่มจากพื้นที่ว่างเปล่า (Blank Floor)
                    </button>
                  </div>
                </div>
              )}
              {/* Floating Scale Calibration Banner */}
              {isCalibratingActive && (
                <div className="absolute top-4 left-1/2 -translate-x-1/2 z-30 bg-gray-900/90 text-white px-4 py-2.5 rounded-full shadow-2xl flex items-center gap-3 backdrop-blur-xs border border-white/20 text-xs pointer-events-auto">
                  <div className="w-2.5 h-2.5 rounded-full bg-amber-400 animate-ping shrink-0" />
                  <span className="font-semibold tracking-wide">
                    {!scaleCalibration.point1 
                      ? '👉 ขั้นตอนที่ 1: คลิกจุด A บนผัง (จุดเริ่มต้นที่ทราบระยะจริง)' 
                      : !scaleCalibration.point2 
                        ? '👉 ขั้นตอนที่ 2: คลิกจุด B บนผัง (จุดสิ้นสุด)' 
                        : '✓ ระบุครบ 2 จุดแล้ว! กรุณากรอกระยะทางจริง (มม.) ในแผงด้านขวาแล้วกดยืนยัน'}
                  </span>
                  {scaleCalibration.point1 && (
                    <button 
                      type="button"
                      onClick={() => setScaleCalibration(p => ({ ...p, point1: null, point2: null }))}
                      className="ml-1 text-[11px] text-amber-300 hover:text-amber-100 underline font-medium cursor-pointer"
                    >
                      รีเซ็ตจุด
                    </button>
                  )}
                  <button 
                    type="button"
                    onClick={cancelCalibration}
                    className="text-[11px] text-gray-400 hover:text-red-300 ml-1 cursor-pointer font-medium"
                  >
                    ✕ ยกเลิก
                  </button>
                </div>
              )}

              {/* Floating Toast Notification Banner */}
              {toast && (
                <div className={`absolute top-4 left-1/2 -translate-x-1/2 z-50 px-4 py-2 rounded-full shadow-2xl text-xs font-semibold flex items-center gap-2 backdrop-blur-xs border transition-all duration-200 pointer-events-none ${
                  toast.type === 'success' ? 'bg-emerald-600/95 text-white border-emerald-400/40' :
                  toast.type === 'warning' ? 'bg-amber-600/95 text-white border-amber-400/40' :
                  'bg-gray-900/95 text-white border-gray-700/50'
                }`}>
                  <span>{toast.msg}</span>
                </div>
              )}

              {/* Floating Multi-Selection Indicator */}
              {selectedCount > 1 && !toast && (
                <div className="absolute top-4 left-1/2 -translate-x-1/2 z-30 bg-blue-600/95 text-white text-xs font-medium px-4 py-1.5 rounded-full shadow-lg flex items-center gap-2 backdrop-blur-xs border border-blue-400/40 pointer-events-none">
                  <span className="font-bold bg-white text-blue-700 rounded-full w-4 h-4 flex items-center justify-center text-[10px]">{selectedCount}</span>
                  <span>เลือกอยู่ {selectedCount} รายการ • ลากย้ายพร้อมกันได้ • Ctrl+C คัดลอก • Del ลบ</span>
                </div>
              )}

              <canvas ref={canvasRef} tabIndex={0} width={800} height={600} className={`absolute inset-0 w-full h-full block focus:outline-none ${cursorClass} ${displayMode === '3d' ? 'hidden' : ''}`}
                onClick={handleCanvasClick} onMouseDown={handleMouseDown} onMouseMove={handleMouseMove} onMouseUp={handleMouseUp} onMouseLeave={handleMouseUp}
                onContextMenu={e => e.preventDefault()}
              onWheel={(e) => {
                if (true) {
                  e.preventDefault();
                  const zoomFactor = Math.exp(-e.deltaY * 0.0005);
                  const prev = viewTransformRef.current;
                  const rect = canvasRef.current.getBoundingClientRect();
                  const scaleX = 800 / rect.width;
                  const scaleY = 600 / rect.height;
                  const mx = (e.clientX - rect.left) * scaleX;
                  const my = (e.clientY - rect.top) * scaleY;
                  const newScale = Math.min(Math.max(0.01, prev.scale * zoomFactor), 5000);
                  viewTransformRef.current = {
                    x: mx - (mx - prev.x) * (newScale / prev.scale),
                    y: my - (my - prev.y) * (newScale / prev.scale),
                    scale: newScale
                  };
                  applyViewTransform();
                } else {
                  const rect = canvasRef.current.getBoundingClientRect();
                  const scaleX = 800 / rect.width;
                  const scaleY = 600 / rect.height;
                  viewTransformRef.current.x -= e.deltaX * scaleX;
                  viewTransformRef.current.y -= e.deltaY * scaleY;
                  applyViewTransform();
                }
              }}
            />
            
            {/* 3D Viewer Layer */}
            {displayMode === '3d' && (
              <div className="absolute inset-0 w-full h-full z-10" style={{ overflow: 'hidden' }}>
                <Map3DViewer
                  mapData={mapData}
                  setMapData={setMapData}
                  scaleCalibration={scaleCalibration}
                  svgBlobUrl={svgBlobUrl}
                  selectedArea={selectedArea}
                  setSelectedArea={setSelectedArea}
                  selectedAreaIds={selectedAreaIds}
                  setSelectedAreaIds={setSelectedAreaIds}
                  selectedCorridor={selectedCorridor}
                  setSelectedCorridor={setSelectedCorridor}
                  selectedCorridorIds={selectedCorridorIds}
                  setSelectedCorridorIds={setSelectedCorridorIds}
                  updateAreaProp={updateAreaProp}
                  deleteArea={deleteArea}
                  showToast={showToast}
                  isPositionLocked={is3DPositionLocked}
                  setIsPositionLocked={setIs3DPositionLocked}
                  showDimensions={show3DDimensions}
                  setShowDimensions={setShow3DDimensions}
                  cameraPreset={cameraPreset}
                />
              </div>
            )}
            </div>
            
            {/* Zoom & Editing Controls Overlay (Hidden in 3D Mode) */}
            {displayMode !== '3d' && (
            <div className="absolute bottom-4 left-4 flex bg-white border border-gray-200 shadow-md rounded-lg overflow-hidden z-20 divide-x divide-gray-100">
              <button onClick={() => { viewTransformRef.current.scale = Math.min(viewTransformRef.current.scale * 1.5, 5000); applyViewTransform(); }} className="px-3 py-1.5 hover:bg-gray-100 font-bold text-gray-700">+</button>
              <div id="zoom-percentage-display" className="px-3 py-1.5 text-sm text-gray-600 flex items-center font-medium">{Math.round(viewTransformRef.current.scale * 100)}%</div>
              <button onClick={() => { viewTransformRef.current.scale = Math.max(viewTransformRef.current.scale / 1.5, 0.1); applyViewTransform(); }} className="px-3 py-1.5 hover:bg-gray-100 font-bold text-gray-700">-</button>
              <button onClick={() => { viewTransformRef.current = { x: 0, y: 0, scale: 1 }; applyViewTransform(); }} className="px-3 py-1.5 hover:bg-gray-100 text-xs font-medium text-gray-600">Reset</button>
              <button onClick={() => setMode('select')} className={`px-3 py-1.5 text-xs font-semibold flex items-center gap-1.5 transition-colors ${mode === 'select' ? 'bg-blue-600 text-white' : 'hover:bg-gray-100 text-gray-700'}`}><MousePointer2 size={13} /> เลือก (Select)</button>
              <button onClick={() => setMode(mode === 'pan' ? 'select' : 'pan')} className={`px-3 py-1.5 text-xs font-medium flex items-center gap-1 transition-colors ${mode === 'pan' ? 'bg-blue-100 text-blue-700' : 'hover:bg-gray-100 text-gray-700'}`}><Hand size={13}/> เลื่อนจอ (Pan)</button>
              <button onClick={() => setMode(mode === 'zoom_window' ? 'select' : 'zoom_window')} className={`px-3 py-1.5 text-xs font-medium transition-colors ${mode === 'zoom_window' ? 'bg-blue-100 text-blue-700' : 'hover:bg-gray-100 text-gray-700'}`}>Fit Region</button>
              {scaleCalibration.pixelsPerMm && (
                <button onClick={() => setMode(mode === 'measure' ? 'select' : 'measure')} className={`px-3 py-1.5 text-xs font-medium flex items-center gap-1 transition-colors ${mode === 'measure' ? 'bg-indigo-100 text-indigo-700' : 'hover:bg-gray-100 text-indigo-600'}`}>
                  <Ruler size={13}/> วัดระยะ (Measure)
                </button>
              )}
              {activeStep === 5 ? (
                <button onClick={() => { setMode('draw_corridor'); setSelectedCorridor(null); }} className={`px-3 py-1.5 text-xs font-medium flex items-center gap-1 transition-colors ${mode === 'draw_corridor' ? 'bg-amber-500 text-white' : 'hover:bg-gray-100 text-gray-700'}`}><Move size={13} /> วาดทางเดิน</button>
              ) : (
                <button onClick={() => { setMode('draw_area'); setSelectedArea(null); setActiveStep(4); }} className={`px-3 py-1.5 text-xs font-medium flex items-center gap-1 transition-colors ${mode === 'draw_area' ? 'bg-blue-600 text-white' : 'hover:bg-gray-100 text-gray-700'}`}><Square size={13} /> วาดพื้นที่</button>
              )}
              {mapData.svgContent && activeStep === 1 && (
                <>
                  <button onClick={() => setMode(mode === 'crop' ? 'select' : 'crop')} className={`px-3 py-1.5 text-xs font-medium flex items-center gap-1 transition-colors ${mode === 'crop' ? 'bg-purple-100 text-purple-700' : 'hover:bg-gray-100 text-purple-600'}`}>
                    <Crop size={13} /> Focus (Crop)
                  </button>
                  {displayMode === 'edit' && (
                    <button onClick={() => setMode('erase')} className={`px-3 py-1.5 text-xs font-medium flex items-center gap-1 transition-colors ${mode === 'erase' ? 'bg-orange-100 text-orange-700' : 'hover:bg-gray-100 text-orange-600'}`}>
                      <Trash2 size={13} /> Erase Lines
                    </button>
                  )}
                </>
              )}

              {/* Selection Actions (Copy, Duplicate, Delete) */}
              {hasSelectedItems && (
                <>
                  <button
                    onClick={copySelected}
                    title="คัดลอกรายการที่เลือก (Ctrl+C)"
                    className="px-3 py-1.5 text-xs font-semibold flex items-center gap-1 text-blue-700 bg-blue-50 hover:bg-blue-100 transition-colors cursor-pointer"
                  >
                    <Copy size={13} /> คัดลอก ({selectedCount})
                  </button>
                  <button
                    onClick={duplicateSelected}
                    title="ทำซ้ำรายการที่เลือก (Ctrl+D)"
                    className="px-3 py-1.5 text-xs font-semibold flex items-center gap-1 text-indigo-700 bg-indigo-50 hover:bg-indigo-100 transition-colors cursor-pointer"
                  >
                    <Copy size={13} /> ทำซ้ำ (Ctrl+D)
                  </button>
                  <button
                    onClick={deleteSelected}
                    title="ลบรายการที่เลือก (Delete)"
                    className="px-3 py-1.5 text-xs font-semibold flex items-center gap-1 text-red-600 bg-red-50 hover:bg-red-100 transition-colors cursor-pointer"
                  >
                    <Trash2 size={13} /> ลบ
                  </button>
                </>
              )}

              {/* Paste Action */}
              {hasClipboard && (
                <button
                  onClick={pasteSelected}
                  title="วางรายการที่คัดลอก (Ctrl+V)"
                  className="px-3 py-1.5 text-xs font-semibold flex items-center gap-1 text-emerald-700 bg-emerald-50 hover:bg-emerald-100 transition-colors cursor-pointer"
                >
                  <ClipboardPaste size={13} /> วาง (Ctrl+V)
                </button>
              )}
            </div>
            )}
            
            {/* Node Popup */}
            {selectedNode && activeStep === 5 && mode === 'select' && (
              <div 
                className="absolute bg-white shadow-xl border border-gray-200 rounded-lg p-3 z-10 w-56 text-sm transform -translate-y-1/2 translate-x-2"
                style={{ left: selectedNode.x + 10, top: selectedNode.y }}
              >
                <div className="flex justify-between items-center mb-2">
                  <h4 className="font-semibold text-gray-800">Node Connections</h4>
                  <button onClick={() => setSelectedNode(null)} className="text-gray-400 hover:text-gray-600"><X size={14}/></button>
                </div>
                {autoConnections.filter(c => c.type === 'corridor-station' && c.fromId === selectedNode.corridorId && selectedNode.connectedAreaIds?.includes(c.toId)).length > 0 ? (
                  <div className="space-y-2">
                    {autoConnections.filter(c => c.type === 'corridor-station' && c.fromId === selectedNode.corridorId && selectedNode.connectedAreaIds?.includes(c.toId)).map(conn => {
                      const nodeConnKey = [selectedNode.id, conn.toId].sort().join('__');
                      return (
                        <div key={conn.key} className="flex items-center justify-between bg-gray-50 p-2 rounded border">
                          <span className="truncate mr-2 font-medium">{conn.toLabel}</span>
                          <button onClick={() => { deleteConnection(nodeConnKey); setSelectedNode(null); }} className="text-red-600 hover:bg-red-50 p-1 rounded text-xs font-semibold px-2 border border-red-200">Disconnect</button>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-xs text-gray-500 italic">No active station connections.</p>
                )}
              </div>
            )}
          </div>
        </div>
        {/* Porsche Configurator Sidebar */}
        <div className="w-96 border-l bg-white flex flex-col shrink-0 h-full overflow-hidden shadow-lg z-10">
          <PorscheConfiguratorPanel
            mapData={mapData}
            setMapData={setMapData}
            scaleCalibration={scaleCalibration}
            setScaleCalibration={setScaleCalibration}
            isCalibrating={isCalibratingActive}
            setIsCalibrating={setIsCalibrating}
            confirmCalibration={confirmCalibration}
            cancelCalibration={cancelCalibration}
            selectedArea={selectedArea}
            setSelectedArea={setSelectedArea}
            selectedAreaIds={selectedAreaIds}
            setSelectedAreaIds={setSelectedAreaIds}
            updateAreaProp={updateAreaProp}
            deleteArea={deleteArea}
            selectedCorridor={selectedCorridor}
            setSelectedCorridor={setSelectedCorridor}
            selectedCorridorIds={selectedCorridorIds}
            setSelectedCorridorIds={setSelectedCorridorIds}
            updateCorridorProp={updateCorridorProp}
            deleteCorridor={deleteCorridor}
            copySelected={copySelected}
            pasteSelected={pasteSelected}
            duplicateSelected={duplicateSelected}
            deleteSelected={deleteSelected}
            autoConnections={autoConnections}
            deleteConnection={deleteConnection}
            regeneratePaths={regeneratePaths}
            mode={mode}
            setMode={setMode}
            activeStep={activeStep}
            setActiveStep={setActiveStep}
            activeRackType={activeRackType}
            setActiveRackType={setActiveRackType}
            activeLaneType={activeLaneType}
            setActiveLaneType={setActiveLaneType}
            getPalletCapacity={getPalletCapacity}
            handleImageUpload={handleImageUpload}
            isUploading={isUploading}
            selectingForFlow={selectingForFlow}
            setSelectingForFlow={setSelectingForFlow}
            createBlankWarehouse={createBlankWarehouse}
            activeSection={activeSection}
            setActiveSection={setActiveSection}
            onNavigateTab={onNavigateTab}
          />
        </div>
      </div>
    </div>
  );
}

// ─── Sub-components ──────────────────────────────────────────────────
function ToolBtn({ active, onClick, icon, label, color = 'blue' }) {
  const colors = color === 'amber'
    ? (active ? 'bg-amber-100 text-amber-700 font-medium' : 'hover:bg-gray-100')
    : (active ? 'bg-blue-100 text-blue-700 font-medium' : 'hover:bg-gray-100');
  return <button className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-sm transition ${colors}`} onClick={onClick}>{icon} {label}</button>;
}

function MiniStep({ num, label, done, active }) {
  return (
    <div className={`flex items-center gap-2 p-2 rounded-lg ${active ? 'bg-blue-50 border border-blue-200' : 'bg-gray-50'}`}>
      <div className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${done ? 'bg-green-500 text-white' : active ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-500'}`}>{done ? '✓' : num}</div>
      <span className={`text-xs font-medium ${active ? 'text-blue-700' : 'text-gray-600'}`}>{label}</span>
    </div>
  );
}
